import React from "react";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import { WorkspaceBand, WorkspaceSummary, WorkspaceTab } from "./billing/WorkspaceBand.jsx";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  AlertTriangle, Archive, Building2, CheckCircle2, ChevronRight, Download, FileText,
  LoaderCircle, Pencil, Plus, Receipt, RefreshCw, Search, Send, Trash2, UserRound, X,
} from "lucide-react";
import {
  archiveBillingAccount,
  buildBillingAccountPayload,
  buildIssuerSettingsPayload,
  createBillingAccount,
  createBillingDrafts,
  deleteBillingAccountLogo,
  deleteDraftInvoice,
  deleteIssuerLogo,
  downloadInvoicePdf,
  downloadInvoiceXlsx,
  fetchBillableItemSummary,
  fetchBillableItems,
  fetchBillingAccounts,
  fetchInvoice,
  fetchInvoiceIssueChecks,
	removeDraftInvoiceItem,
  fetchInvoices,
	fetchInvoiceSummary,
  fetchIssuerSettings,
	createDraftManualLine,
	removeDraftManualLine,
  fetchSourceClients,
  issueInvoice,
  previewBillingDrafts,
  sendInvoice,
	updateDraftInvoice,
  updateBillingAccount,
  updateBillingAccountAssociations,
  updateIssuerSettings,
  uploadBillingAccountLogo,
  uploadIssuerLogo,
  voidInvoice,
} from "../api/billingApi.js";
import useAccess from "../hooks/useAccess.jsx";
import ConsolidationTariffFields from "./billing/ConsolidationTariffFields.jsx";
import BillingLogoField from "./billing/BillingLogoField.jsx";
import useBillingPreferences from "../hooks/useBillingPreferences.js";
import useBillingLiveRefresh from "../hooks/useBillingLiveRefresh.js";
import InvoiceConsolidationNotice from "./billing/InvoiceConsolidationNotice.jsx";
import { formatQuantityTons } from "../hooks/useDataManagerOrdersUtils.js";
import { deleteClientCorrespondence, updateClientCorrespondence } from "../lib/dataManagerApi.js";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import { createBillingLoadRequestGuard } from "../lib/billingLoadRequestGuard.js";
import { createInvoiceIssueChecksCoordinator, mergeInvoiceIssueCheckResults } from "../lib/billingIssueChecks.js";
import { readOrderDataRevision } from "../utils/orderDataSync.js";
import {
  APPROVED_BLOCKED_FILTER_CODE,
  billableBlockedReasonFilterOptions,
  billableBlockingReasons,
  billableFilterClients,
  billableKpisFromSummary,
  billablePreviewItemLabel,
  billableSourceLabel,
  billingAccountLabel,
  billingEmptyState,
  filterBillableItemsByOverdue,
  billingUnitLabel,
  groupInvoiceLinesForDisplay,
  invoiceTimeLabel,
  invoiceTonneRate,
  invoiceLineServiceLabel,
  invoiceOrderReferenceLabel,
  isManualInvoiceLine,
  invoiceIssueBlockingReasons,
  invoiceIssueChecksPending,
  invoiceNeedsRevalidation,
  CREATE_BILLING_ACCOUNT_OPTION_LABEL,
  CREATE_BILLING_ACCOUNT_OPTION_VALUE,
  isBillingSetupIncomplete,
  isCreateBillingAccountOption,
  INCOMPLETE_EMAIL_MESSAGE,
  isCompleteEmail,
  issuerSettingsIssues,
  resolveBillingAccountSaveDisabledMessages,
} from "../utils/billingPresentation.js";
import { focusBillingDialogElement, handleBillingDialogKeyDown } from "../utils/billingDialogAccessibility.js";
import { confirmInGrain } from "../utils/grainDialogs.js";
import { buildBillingDraftSelection } from "../utils/billingDraftSelection.js";
import {
  createDefaultBillableFilters,
  createDefaultInvoiceFilters,
  createDefaultAccountsFilters,
  isDefaultBillableFilters,
} from "../utils/billingPreferences.js";
import { useToast, HoverCard } from "./ResultsPrimitives.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";
import BillableKpiStrip from "./billing/BillableKpiStrip.jsx";
import BillableAccountCards from "./billing/BillableAccountCards.jsx";
import BillingFiltersContextMenu from "./billing/BillingFiltersContextMenu.jsx";
import InvoiceCards from "./billing/InvoiceCards.jsx";
import InvoiceKpiStrip from "./billing/InvoiceKpiStrip.jsx";
import InvoiceBlockingReasons from "./billing/InvoiceBlockingReasons.jsx";
import InvoiceSendChecks from "./billing/InvoiceSendChecks.jsx";
import InvoiceGpsPanel, { useInvoiceGps } from "./billing/InvoiceGpsProof.jsx";
import { isTransportStatement } from "../utils/transportStatement.js";
import InvoicePeriodEditor from "./billing/InvoicePeriodEditor.jsx";
import { lastWeekPeriod, monthPeriod, periodLabel, periodMonth, recentMonths, serviceDaysLabel, yesterdayPeriod } from "../utils/billingPeriods.js";
import { changeInvoiceOrderRates, changeInvoicePeriod } from "../api/consolidationApi.js";
import { InvoiceLineGroupRows, InvoiceLinesOverview, useInvoiceLineGroups } from "./billing/InvoiceLineGroups.jsx";
import InvoiceOrderLines from "./billing/InvoiceOrderLines.jsx";
import { InvoiceReturnDetailsRow, InvoiceReturnToggle, useInvoiceReturnDetails } from "./billing/InvoiceReturnDetails.jsx";
import BillingAssociationsEditor, { BillingAssociationPicker } from "./billing/BillingAliasesView.jsx";
import useBillingAccountAssociations from "../hooks/useBillingAccountAssociations.js";
import useBillingAliasesLeaveGuard from "../hooks/useBillingAliasesLeaveGuard.js";
import BillingDirectoryView, { archiveConfirmation } from "./billing/BillingDirectoryView.jsx";
import { buildBillingDirectory, billingDirectoryAccountDraft, billingDirectoryAssociationPayload } from "../utils/billingDirectory.js";
import { buttonToneClasses, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME, joinClasses } from "./resultsPrimitives/shared.js";

const TABS = [
  { key: "billable", label: "À facturer", icon: Receipt },
  { key: "invoices", label: "Relevés", icon: FileText },
  { key: "accounts", label: "Entreprises de facturation", icon: Building2 },
];
const BILLABLE_STATUS_FILTERS = [
  { value: "", label: "Tous les états" },
  { value: "eligible", label: "Prêts" },
  { value: "blocked", label: "Bloqués" },
];
const BILLING_PILL_CLASS_NAME = DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME.replace("!", "");
const STATUS_SUB_NAV_BUTTON = joinClasses([
  "inline-flex h-8 items-center border px-3 text-xs font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45",
  BILLING_PILL_CLASS_NAME,
]);
const INPUT = joinClasses([
  "h-10 w-full border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-slate-100 disabled:text-slate-500",
  BILLING_PILL_CLASS_NAME,
]);
const COMPACT_INPUT = joinClasses([
  "h-9 w-full border border-slate-300 bg-white px-2.5 text-sm text-slate-800 outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-slate-100 disabled:text-slate-500",
  BILLING_PILL_CLASS_NAME,
]);
const LINE_NUMBER_INPUT = joinClasses([
  "h-7 w-20 border border-slate-300 bg-white px-1 text-right text-xs text-slate-800 outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-slate-100 disabled:text-slate-500",
  BILLING_PILL_CLASS_NAME,
]);
const INVOICE_HEADER_INPUT = joinClasses([
  "h-7 w-full border border-slate-300 bg-white px-2 text-xs text-slate-800 outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-slate-100 disabled:text-slate-500",
  BILLING_PILL_CLASS_NAME,
]);
const BUTTON = joinClasses([
  "inline-flex h-10 items-center justify-center gap-1.5 border px-3.5 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-45",
  BILLING_PILL_CLASS_NAME,
]);
const PRIMARY_BUTTON = joinClasses([BUTTON, buttonToneClasses.optimizer]);
const SECONDARY_BUTTON = `${BUTTON} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
const DANGER_BUTTON = `${BUTTON} border-red-300 bg-red-50 text-red-700 hover:bg-red-100`;
// Confirmer l'annulation d'un relevé : brique pleine (aucun fond pâle).
const CANCEL_CONFIRM_BUTTON = `${BUTTON} border-[#c5524a] bg-[#c5524a] text-white hover:bg-[#b0473f]`;
const BILLING_SAVE_BUTTON_DISABLED_CLASS_NAME = "pointer-events-none cursor-not-allowed opacity-60";
const STEP_BADGE = joinClasses([
  "grid h-6 w-6 shrink-0 place-items-center bg-[var(--color-brand)] text-xs font-extrabold text-white",
  BILLING_PILL_CLASS_NAME,
]);
const BILLING_CHECKBOX_CLASS_NAME = "h-4 w-4 shrink-0 rounded border-slate-300 accent-accent text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40";

const asItems = (payload) => Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
const clean = (value) => String(value ?? "").trim();
const numberValue = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const display = (value, fallback = "—") => clean(value) || fallback;

function formatInvoiceLineDecimal(value, digits = 1) {
  if (value === "" || value === null || value === undefined) return "";
  const numeric = numberValue(value);
  if (!Number.isFinite(numeric)) return "";
  return numeric.toFixed(digits);
}

function formatMoney(value, currency = "CAD") {
  if (value == null) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: currency || "CAD" }).format(numberValue(value));
}

function formatNumber(value, digits = 2) {
  return new Intl.NumberFormat("fr-CA", { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(numberValue(value));
}

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(String(value).length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return display(value);
  const day = new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" }).format(date);
  return withTime ? `${day} ${new Intl.DateTimeFormat("fr-CA", { timeStyle: "short" }).format(date)}` : day;
}


function quantityLabel(value) {
  const quantity = numberValue(value?.quantity ?? value?.actual_tonnage ?? value?.trip_units);
  const unit = value?.billing_unit || (value?.trip_units ? "voyage" : "tonne");
  const digits = unit === "heure" ? 6 : ["voyage", "trip"].includes(clean(unit).toLowerCase()) ? 0 : 1;
  return `${formatNumber(quantity, digits)} ${billingUnitLabel(unit, quantity)}`;
}

function invoiceSourceLabel(invoice) {
  const count = invoice?.source_client?.source_count;
  if (count > 1) return `${count} clients`;
  return billableSourceLabel({
    source_client: invoice?.source_client,
    source_client_snapshot: invoice?.source_client_snapshot,
    source_client_name: invoice?.source_client_name || invoice?.client_name,
  });
}

function invoiceAccountLabel(invoice) {
  return clean(
    invoice?.billing_account_name
      || invoice?.billing_account_snapshot?.name
      || invoice?.billing_account?.name
      || invoice?.client_name,
  ) || "Entreprise introuvable";
}

const invoiceLifecycleStatus = (invoice) => invoice?.lifecycle_status || invoice?.status || "";

function isManualInvoiceDelivery(delivery) {
  return clean(delivery?.channel).toLowerCase() === "manual";
}

function EmptyState({ error = "", loading = false, message, description, onRetry, onReset, action = null }) {
  if (loading) return <div className="grid min-h-60 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><LoaderCircle className="animate-spin" size={18} /> Chargement…</span></div>;
  if (error) return <div className="grid min-h-60 place-items-center p-6 text-center"><div><AlertTriangle className="mx-auto mb-2 text-red-500" size={24} /><p className="font-bold text-red-700">{error}</p><button type="button" className={`${SECONDARY_BUTTON} mt-3`} onClick={onRetry}>Réessayer</button></div></div>;
  return <div className="grid min-h-60 place-items-center p-6 text-center"><div><p className="font-extrabold text-slate-800">{message}</p>{description ? <p className="mt-1 text-xs text-slate-600">{description}</p> : null}{onReset ? <button type="button" className={`${SECONDARY_BUTTON} mt-3`} onClick={onReset}>Effacer les filtres</button> : null}{action ? <button type="button" className={`${SECONDARY_BUTTON} mt-3`} onClick={action.onClick}>{action.label}</button> : null}</div></div>;
}

function Modal({ children, onClose, title, subtitle = "", wide = false, medium = false, fillBody = false, compact = true, maxWidthClassName, sidePanel = false, closeDisabled = false }) {
  const dialogRef = React.useRef(null);
  const closeButtonRef = React.useRef(null);
  const restoreFocusRef = React.useRef(typeof document !== "undefined" ? document.activeElement : null);
  React.useEffect(() => {
    focusBillingDialogElement(closeButtonRef.current);
    return () => focusBillingDialogElement(restoreFocusRef.current);
  }, []);
  const maxWidthClass = maxWidthClassName || (fillBody ? "max-w-[1200px]" : wide ? "max-w-6xl" : medium ? "max-w-4xl" : "max-w-2xl");
  return <div className={`fixed inset-0 z-[100] flex bg-slate-950/45 ${sidePanel ? "items-stretch justify-end p-2" : "items-center justify-center p-4"}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !closeDisabled) onClose(); }}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} onKeyDown={(event) => handleBillingDialogKeyDown(event, { activeElement: document.activeElement, dialog: dialogRef.current, onClose: closeDisabled ? null : onClose })} className={`flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl ${maxWidthClass} ${sidePanel ? "!max-h-full h-full rounded-[4px]" : fillBody ? "h-[min(900px,90dvh)] rounded-[4px]" : compact ? "rounded-[4px]" : "rounded-overlay"}`}>
      <header className={`flex items-start justify-between gap-3 border-b border-slate-200 px-4 ${compact ? "py-2" : "py-3"}`}><div><h2 className={`${compact ? "text-sm" : "text-base"} font-extrabold text-slate-900`}>{title}</h2>{subtitle ? <p className={`${compact ? "mt-0" : "mt-0.5"} text-xs text-slate-600`}>{subtitle}</p> : null}</div><button ref={closeButtonRef} type="button" className={joinClasses([compact ? "p-1" : "p-1.5", "text-slate-500 hover:bg-slate-100", BILLING_PILL_CLASS_NAME])} aria-label="Fermer" disabled={closeDisabled} onClick={onClose}><X size={compact ? 16 : 18} /></button></header>
      {children}
    </section>
  </div>;
}

function Field({ label, value, onChange, required = false, type = "text", options, placeholder = "", disabled = false, min, step, compact = false }) {
  const inputClass = compact ? `${INPUT} !h-6 !px-2 !py-0 !text-xs` : INPUT;
  return <label className={`grid gap-1 text-xs font-bold text-slate-700${compact ? " min-w-0" : ""}`}><span>{label}{required ? " *" : ""}</span>{options ? <select className={inputClass} value={value ?? ""} disabled={disabled} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select> : <input className={inputClass} value={value ?? ""} disabled={disabled} type={type} min={min} step={step} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />}</label>;
}

function TextAreaField({ label, value, onChange, required = false, placeholder = "", disabled = false, compact = false }) {
  return <label className="grid gap-1 text-xs font-bold text-slate-700"><span>{label}{required ? " *" : ""}</span><textarea className={`${INPUT} resize-y py-2 ${compact ? "min-h-14" : "min-h-20"}`} rows={compact ? 2 : undefined} value={value ?? ""} disabled={disabled} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} /></label>;
}

function BillingAccountSaveDisabledHoverCardContent({ messages = [] }) {
  if (messages.length <= 1) {
    return <span>{messages[0] || ""}</span>;
  }

  return (
    <ul className="grid list-disc gap-1 pl-4">
      {messages.map((message) => (
        <li key={message}>{message}</li>
      ))}
    </ul>
  );
}

function StatusSubNav({
  options,
  value,
  onChange,
  disabled = false,
  reasonOptions = [],
  reasonValue = "",
  onReasonChange,
}) {
  return (
    <div aria-label="Filtrer par état" className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-slate-200/80 bg-white px-3 py-2.5" role="tablist">
      {options.map((option) => {
        const active = value === option.value && !reasonValue;
        const warningActive = active && option.activeTone === "warning";
        const blockedWithReason = option.value === "blocked" && Boolean(reasonValue) && value === "blocked";
        const selected = active || blockedWithReason;
        return (
          <button
            key={option.value || "all"}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            className={`${STATUS_SUB_NAV_BUTTON} gap-1.5 ${
              selected
                ? (warningActive || blockedWithReason ? "border-amber-600 bg-amber-600 text-white" : "border-accent bg-accent text-white")
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
            onClick={() => onChange(option.value)}
          >
            <span>{option.label}</span>
          </button>
        );
      })}
      {reasonOptions.length ? (
        <>
          <span aria-hidden="true" className="mx-1 hidden h-5 w-px bg-slate-200 sm:inline-block" />
          {reasonOptions.map((option) => {
            const active = reasonValue === option.code;
            return (
              <button
                key={option.code}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                title={option.label}
                className={`${STATUS_SUB_NAV_BUTTON} gap-1.5 ${
                  active
                    ? "border-amber-600 bg-amber-600 text-white"
                    : "border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300"
                }`}
                onClick={() => onReasonChange?.(option.code)}
              >
                <span>{option.label}</span>
              </button>
            );
          })}
        </>
      ) : null}
    </div>
  );
}

function Filters({ activeTab, sourceClients, clientNames = [], accounts = [], filters, onChange, onReset }) {
  // Les filtres de départ d'« À facturer » n'ont rien à effacer : « Effacer » les remettrait tels quels.
  const hasFilters = !(activeTab === "billable" && isDefaultBillableFilters(filters))
    && Boolean(filters.search || filters.billingSide || filters.status || filters.clientId || filters.clientName || filters.billingAccountId || filters.fromDate || filters.toDate || filters.overdueOnly || filters.blockingReason);
  // Relevés : la ferme est dans les commandes, la coop reçoit le relevé ; un mois entier remplace les deux dates.
  const statements = activeTab === "invoices";
  const quickPeriods = activeTab === "billable" ? [["Semaine dernière", lastWeekPeriod()], ["Hier", yesterdayPeriod()]] : [];
  return <div className="flex flex-wrap items-end gap-2 border-b border-slate-200/80 bg-white px-3 py-2.5">
    <label className="relative min-w-60 flex-1"><Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} /><input aria-label="Recherche" className={`${INPUT} pl-8`} value={filters.search} placeholder={statements ? "Relevé, entreprise, client ou commande…" : "Commande, client ou entreprise…"} onChange={(event) => onChange("search", event.target.value)} /></label>
    {statements ? <>
      <select aria-label="Client" className={`${INPUT} !w-52`} value={filters.clientName || ""} onChange={(event) => onChange("clientName", event.target.value)}><option value="">Tous les clients</option>{clientNames.map((name) => <option key={name} value={name}>{name}</option>)}</select>
      <select aria-label="Entreprise facturée" className={`${INPUT} !w-52`} value={filters.billingAccountId || ""} onChange={(event) => onChange("billingAccountId", event.target.value)}><option value="">Toutes les entreprises</option>{accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select>
      <select aria-label="Mois" className={`${INPUT} !w-44`} value={periodMonth(filters.fromDate, filters.toDate)} onChange={(event) => { const period = monthPeriod(event.target.value); onChange({ fromDate: period.from, toDate: period.to }); }}><option value="">Tous les mois</option>{recentMonths().map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}</select>
    </> : <>
      <select aria-label="Client Data Manager" className={`${INPUT} !w-52`} value={filters.clientId} onChange={(event) => onChange("clientId", event.target.value)}><option value="">Tous les clients</option>{billableFilterClients(sourceClients, filters.clientId).map((client) => <option key={client.id} value={client.id}>{client.client_name || client.correspondence_name || client.name || client.client_code}</option>)}</select>
      <input aria-label="Du" className={`${INPUT} !w-36`} type="date" value={filters.fromDate} onChange={(event) => onChange("fromDate", event.target.value)} />
      <input aria-label="Au" className={`${INPUT} !w-36`} type="date" value={filters.toDate} onChange={(event) => onChange("toDate", event.target.value)} />
      {quickPeriods.map(([label, period]) => <button key={label} type="button" className={SECONDARY_BUTTON} aria-pressed={filters.fromDate === period.from && filters.toDate === period.to} title={`Prestations ${periodLabel(period.from, period.to)} ; le brouillon créé couvrira ces jours.`} onClick={() => onChange({ fromDate: period.from, toDate: period.to })}>{label}</button>)}
    </>}
    {hasFilters ? <button type="button" className={SECONDARY_BUTTON} onClick={onReset}><X size={13} />Effacer</button> : null}
  </div>;
}

function Pager({ meta, count, onPage }) {
  const offset = numberValue(meta?.offset ?? meta?.cursor);
  const limit = Math.max(1, numberValue(meta?.limit) || 100);
  const total = numberValue(meta?.total ?? count);
  const next = meta?.next_cursor;
  if (offset <= 0 && !next) return null;
  return <footer className="flex min-h-10 items-center justify-between border-t border-slate-200 bg-slate-50 px-3 text-xs text-slate-600"><span>{count ? `${offset + 1}–${offset + count} sur ${total}` : "0 résultat"}</span><div className="flex gap-2"><button type="button" className={SECONDARY_BUTTON} disabled={offset <= 0} onClick={() => onPage(String(Math.max(0, offset - limit)))}>Précédent</button><button type="button" className={SECONDARY_BUTTON} disabled={!next} onClick={() => onPage(String(next))}>Suivant</button></div></footer>;
}

function draftPreviewBlockingItems(preview) {
  const items = asItems(preview?.items);
  const blocking = asItems(preview?.blocking_items || preview?.blocked_items || preview?.blocks);
  return blocking.length ? blocking : items.filter((item) => item.eligible === false || billableBlockingReasons(item).length);
}

// Toutes les prestations entrent dans des brouillons qui couvrent déjà leur date : « Ajouter au brouillon ».
function addsToExistingDrafts(preview) {
  const drafts = asItems(preview?.projected_drafts);
  return drafts.length > 0 && drafts.every((draft) => draft.existing_draft);
}

function DraftPreviewModal({ preview, busy, onClose, onConfirm }) {
  const drafts = asItems(preview?.projected_drafts || preview?.drafts);
  const items = asItems(preview?.items);
  const derivedBlocking = draftPreviewBlockingItems(preview);
  // Causes générales du serveur (sans prestation désignée) et avis qui laissent créer le brouillon.
  const causes = asItems(preview?.issues).filter((issue) => !issue.entity_id);
  const warnings = asItems(preview?.warnings);
  const blockingCount = Math.max(derivedBlocking.length, numberValue(preview?.blocking_count ?? preview?.blocked_count));
  const readyCount = numberValue(preview?.ready_count ?? preview?.eligible_count ?? (preview?.candidate_count !== undefined ? numberValue(preview.candidate_count) - numberValue(preview.blocking_count) : items.length - derivedBlocking.length));
  const adding = addsToExistingDrafts(preview);
  const draftCount = drafts.length || numberValue(preview?.projected_draft_count);
  return <Modal title="Vérifier les brouillons" subtitle="Aucun relevé ne sera envoyé automatiquement." onClose={onClose} medium compact>
    <div className="min-h-0 overflow-x-hidden overflow-y-auto p-5 text-sm">
      {/* Période de chaque brouillon visé, décidée par le serveur : celle du brouillon existant, ou les jours libres de « Du » et « Au ». */}
      {drafts.map((draft, index) => draft.period_start ? <p key={`${draft.billing_account_id}-${draft.existing_draft?.id || "nouveau"}-${index}`} className="mb-3 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <strong>{draft.existing_draft ? "Ajout au brouillon existant :" : "Période des prestations :"}</strong> du {formatDate(draft.period_start)} au {formatDate(draft.period_end)}
      </p> : null)}
      <div className="grid gap-2">
        <div className="flex items-baseline gap-2 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2">
          <strong className="text-lg font-extrabold text-slate-900">{draftCount}</strong>
          <span className="text-sm text-slate-600">{draftCount > 1 ? "brouillons distincts" : "brouillon distinct"}</span>
        </div>
      </div>
      {drafts.length ? (
        <div className="mt-4 overflow-hidden rounded-[4px] border border-slate-200">
          <table className="!min-w-0 w-full table-fixed text-left text-sm [&_thead_th]:!text-[0.8rem] [&_thead_th]:!tracking-[0.06em] [&_tbody_td]:!text-sm">
            <colgroup>
              <col className="w-[65%]" />
              <col className="w-[35%]" />
            </colgroup>
            <thead className="bg-slate-100">
              <tr>
                <th className="p-2.5">Entreprise</th>
                <th className="whitespace-nowrap p-2.5 text-right">{adding ? "Ajouté" : "Avant taxes"}</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft, index) => {
                const accountName = draft.billing_account_name || draft.account_name || "—";
                return (
                  <tr key={draft.key || `${draft.billing_account_id}-${draft.existing_draft?.id || "nouveau"}-${index}`}>
                    <td className="border-t p-2.5"><span className="block truncate" title={accountName}>{accountName}</span></td>
                    <td className="border-t p-2.5 text-right font-bold tabular-nums whitespace-nowrap">{formatMoney(draft.subtotal, draft.currency_code)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {warnings.map((warning, index) => <p key={`${warning.code}-${index}`} className="mt-4 rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{warning.message}</p>)}
      {derivedBlocking.length || causes.length ? (
        <div className="mt-4 rounded-[8px] border border-slate-200 border-l-2 border-l-red-600 bg-white p-3">
          <h3 className="text-sm font-extrabold text-slate-900">À corriger avant la création</h3>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {causes.map((cause, index) => <li key={`${cause.code}-${index}`}>• {cause.message}</li>)}
            {derivedBlocking.map((item, index) => (
              <li key={item.id || index}>
                • {billablePreviewItemLabel(item)} — {billableBlockingReasons(item).join(" · ") || item.message || "Élément bloqué"}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
    <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
      <button type="button" className={SECONDARY_BUTTON} disabled={busy} onClick={onClose}>Annuler</button>
      <button type="button" className={PRIMARY_BUTTON} disabled={busy || readyCount <= 0 || blockingCount > 0} onClick={onConfirm}>
        {busy ? <LoaderCircle className="animate-spin" size={14} /> : <FileText size={14} />}
        {adding ? "Ajouter au brouillon" : "Créer les brouillons"}
      </button>
    </footer>
  </Modal>;
}

function AccountEditor({ account, busy, onClose, onSaved, onReload, onArchive, canEdit, onGuardChange }) {
  const isNew = !account?.id;
  const [draft, setDraft] = React.useState(() => ({
    id: account?.id || "",
    name: account?.name || account?.legal_name || "",
    billing_address: account?.billing_address || "",
    billing_email: account?.billing_email || "",
    statement_link_days: account?.statement_link_days || 90,
    client_ids: [...(account?.client_ids || account?.clients?.map((client) => client.id) || [])],
    normal_hourly_rate: account?.normal_hourly_rate ?? "",
    tonne_supplement_rate: account?.tonne_supplement_rate ?? "",
    voyage_supplement_rate: account?.voyage_supplement_rate ?? "",
    tonne_supplement_enabled: Boolean(account?.tonne_supplement_enabled),
    voyage_supplement_enabled: Boolean(account?.voyage_supplement_enabled),

    version: account?.version,
  }));
  const initialDraft = React.useRef(JSON.stringify(draft));
  const associations = useBillingAccountAssociations({ account, accountName: draft.name, onReload });
  const [associationFocus, setAssociationFocus] = React.useState(0);
  const focusAssociationPicker = () => setAssociationFocus((current) => current + 1);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [needsReload, setNeedsReload] = React.useState(false);
  const [archiveAsked, setArchiveAsked] = React.useState(false);
  const [logoUrl, setLogoUrl] = React.useState(account?.logo_data_url || null);
  const [logoBusy, setLogoBusy] = React.useState(false);
  // Enregistré tout de suite, hors du formulaire : le logo ne change pas la version de l'entreprise.
  const changeLogo = async (file) => {
    setLogoBusy(true);
    try {
      const saved = file ? await uploadBillingAccountLogo(account.id, file) : await deleteBillingAccountLogo(account.id);
      setLogoUrl(saved.logo_data_url || null);
      await onReload();
    } finally { setLogoBusy(false); }
  };
  const committedAccountRef = React.useRef(null);
  const savingRef = React.useRef(false);
  const dirty = JSON.stringify(draft) !== initialDraft.current || associations.dirty;
  const allowLeave = useBillingAliasesLeaveGuard({
    dirty: canEdit && dirty && !committedAccountRef.current, saving,
    discard: () => {}, onGuardChange,
    message: "Des modifications de cette entreprise ne sont pas enregistrées. Les abandonner et fermer la fiche ?",
  });
  const close = async () => { if (!savingRef.current && await allowLeave()) onClose(); };
  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const saveDisabledMessages = React.useMemo(
    () => resolveBillingAccountSaveDisabledMessages({ busy, draft, saving }),
    [busy, draft, saving],
  );
  const canSave = saveDisabledMessages.length === 0 && associations.ready;
  const showSaveDisabledHoverCard = !canSave && saveDisabledMessages.length > 0;
  const save = async () => {
    if (savingRef.current || needsReload || !canSave || !canEdit) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const payload = buildBillingAccountPayload(draft);
      if (isNew) {
        Object.assign(payload, associations.payload);
      } else {
        delete payload.client_ids;
        if (associations.payload) payload.association_changes = associations.payload;
      }
      const saved = isNew ? await createBillingAccount(payload) : await updateBillingAccount(account.id, payload);
      committedAccountRef.current = saved;
      setNeedsReload(true);
      await onSaved(saved);
    } catch (error) {
      if (committedAccountRef.current) setSaveError(`Entreprise enregistrée, mais rechargement impossible. ${error.message || ""}`);
      else {
        setSaveError(error.message || "L’entreprise n’a pas pu être enregistrée.");
        // Validation de la Consolidation en cours : rien n'est enregistré, la fiche reste modifiable pour réessayer.
        if ((error.status === 409 || error.status === 404) && error.payload?.detail?.code !== "consolidation_busy") setNeedsReload(true);
      }
    } finally { savingRef.current = false; setSaving(false); }
  };
  const reloadAfterSave = async () => {
    if (savingRef.current || (!committedAccountRef.current && !await allowLeave())) return;
    savingRef.current = true; setSaving(true); setSaveError("");
    try {
      if (committedAccountRef.current) await onSaved(committedAccountRef.current);
      else { await onReload(); onClose(); }
    } catch (error) { setSaveError(`${committedAccountRef.current ? "Entreprise enregistrée, mais rechargement impossible. " : ""}${error.message}`); }
    finally { savingRef.current = false; setSaving(false); }
  };
  const saveButton = (
    <button
      type="button"
      aria-disabled={!canSave ? "true" : undefined}
      className={[
        PRIMARY_BUTTON,
        showSaveDisabledHoverCard ? BILLING_SAVE_BUTTON_DISABLED_CLASS_NAME : "",
      ].join(" ").trim()}
      disabled={!canSave && !showSaveDisabledHoverCard}
      onClick={showSaveDisabledHoverCard ? undefined : save}
    >
      {saving ? <LoaderCircle className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
      Enregistrer
    </button>
  );
  const saveAction = showSaveDisabledHoverCard ? (
    <HoverCard
      accentColor="var(--hovercard-accent-warning)"
      content={<BillingAccountSaveDisabledHoverCardContent messages={saveDisabledMessages} />}
      delay={80}
      maxWidth={360}
      placement="top-end"
      title="Enregistrement impossible"
    >
      <span className="inline-flex cursor-not-allowed">{saveButton}</span>
    </HoverCard>
  ) : saveButton;
  return <>
  <Modal title={isNew ? "Ajouter une entreprise de facturation" : billingAccountLabel(account)} subtitle="Coordonnées, tarifs et associations de cette entreprise." onClose={close} wide fillBody maxWidthClassName={canEdit ? "max-w-[1500px]" : undefined}>
    <fieldset disabled={saving || needsReload} className={["grid min-h-0 min-w-0 flex-1 items-start gap-2.5 overflow-y-auto rounded-[4px] p-3 lg:grid-rows-[minmax(0,1fr)] lg:items-stretch lg:overflow-hidden", isNew || canEdit ? "lg:grid-cols-[minmax(0,1fr)_360px]" : "lg:grid-cols-1"].join(" ")}>
      <div className="min-w-0 space-y-2.5 lg:min-h-0 lg:overflow-y-auto">
          <section className="rounded-[4px] border border-slate-200 p-2.5">
            <h3 className="mb-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500">Coordonnées de facturation</h3>
            <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_12rem]">
              <Field label="Nom de l’entreprise" required value={draft.name} disabled={!canEdit} onChange={(value) => setField("name", value)} />
              <Field label="Courriel de facturation" required type="email" value={draft.billing_email} disabled={!canEdit} onChange={(value) => setField("billing_email", value)} />
              <div className="lg:col-start-3"><Field label="Lien en ligne du relevé" value={String(draft.statement_link_days)} disabled={!canEdit} options={[30, 60, 90, 180, 365].map((days) => ({ value: String(days), label: `${days} jours` }))} onChange={(value) => setField("statement_link_days", Number(value))} /></div>
            </div>
            <div className="mt-1.5">
              <TextAreaField label="Adresse de facturation" required compact value={draft.billing_address} disabled={!canEdit} onChange={(value) => setField("billing_address", value)} />
            </div>
          </section>
          <BillingLogoField title="Logo de l’entreprise" logoUrl={logoUrl} editable={canEdit && !isNew} disabled={busy || saving || logoBusy}
            note={isNew ? "Enregistrez d’abord l’entreprise pour ajouter son logo." : "Affiché sur le relevé de transport envoyé à cette entreprise."}
            onUpload={changeLogo} onRemove={() => changeLogo(null)} />
          <section className="rounded-[4px] border border-slate-200 p-2.5">
            <ConsolidationTariffFields draft={draft} setField={setField} disabled={!canEdit} />
          </section>
        <section className="rounded-[4px] border border-slate-200 p-2.5">
          <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Clients rattachés</h3>
          {associations.loading ? <p role="status" className="p-2 text-xs">Chargement des associations…</p> : associations.error ?
            <div role="alert" className="p-2 text-xs text-red-700"><p>{associations.error}</p>
              <button type="button" className={`${SECONDARY_BUTTON} mt-2`} onClick={reloadAfterSave}>Recharger le répertoire</button></div> :
            associations.draft ? <BillingAssociationsEditor accountId={associations.accountId} canEdit={canEdit}
              busy={busy || saving || needsReload} draft={associations.draft} onDraftChange={associations.setDraft} onPick={focusAssociationPicker} /> : null}
        </section>
      </div>
      {canEdit && associations.draft ? <BillingAssociationPicker accountId={associations.accountId} draft={associations.draft}
        onDraftChange={associations.setDraft} focusKey={associationFocus} busy={busy || saving || needsReload} /> : null}
    </fieldset>
    {saveError ? <p role="alert" className="mx-4 mb-2 shrink-0 rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">{saveError}</p> : null}
    <footer className="flex shrink-0 items-center justify-between border-t border-slate-200 px-4 py-2.5">
      <div>{!isNew && canEdit ? <button type="button" className={DANGER_BUTTON} disabled={busy || saving || needsReload} onClick={async () => { if (await allowLeave()) setArchiveAsked(true); }}><Archive size={14} />Archiver</button> : null}</div>
      <div className="flex gap-2">
        <button type="button" className={SECONDARY_BUTTON} disabled={busy || saving} onClick={close}>{committedAccountRef.current ? "Fermer" : "Annuler"}</button>
        {canEdit ? needsReload ? <button type="button" className={PRIMARY_BUTTON} disabled={busy || saving} onClick={reloadAfterSave}>Recharger le répertoire</button> : saveAction : null}
      </div>
    </footer>
  </Modal>
  {/* Même confirmation que « Archiver… » du clic droit ; « Annuler » laisse la fiche ouverte. */}
  {archiveAsked ? <ConfirmationModal eyebrow="Facturation" cornerRadius="grain" panelSize="narrow" contentDensity="compact" tone="warning" isBusy={busy}
    {...archiveConfirmation(account.name || account.legal_name || "")} onCancel={() => setArchiveAsked(false)}
    onConfirm={() => { setArchiveAsked(false); onArchive(account); }} /> : null}
  </>;
}

function mergeBillingAccounts(existing = [], created = []) {
  const byId = new Map();
  [...existing, ...created].forEach((account) => {
    if (account?.id) byId.set(account.id, account);
  });
  return [...byId.values()];
}

const ISSUER_SETTINGS_TABS = [{ value: "issuer", label: "Entreprise émettrice" }, { value: "billing", label: "Facturation" }];

function IssuerSettingsModal({ settings, busy, canEdit, onClose, onSaved, onLogoChanged }) {
  const [draft, setDraft] = React.useState(() => ({
    legal_name: settings?.legal_name || "",
    address: settings?.address || "",
    phone: settings?.phone || "",
    email: settings?.email || "",
    gst_registration_number: settings?.gst_registration_number || "",
    qst_registration_number: settings?.qst_registration_number || "",
    gst_rate_percent: settings?.gst_rate_percent ?? "5.000",
    qst_rate_percent: settings?.qst_rate_percent ?? "9.975",
    taxes_enabled: Boolean(settings?.taxes_enabled),
    aspiration_fixed_rate: settings?.aspiration_fixed_rate != null ? String(Number(settings.aspiration_fixed_rate)) : "35",
    aspiration_fixed_rate_scope: settings?.aspiration_fixed_rate_scope || "order",
    fuel_surcharge_percent: String(Number(settings?.fuel_surcharge_percent || 0)),
    dompeur_tonne_rate: settings?.dompeur_tonne_rate != null ? String(Number(settings.dompeur_tonne_rate)) : "",
    version: settings?.version,
  }));
  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const issuerComplete = Boolean(clean(draft.legal_name) && clean(draft.address));
  // Une entreprise déjà configurée ouvre directement ses règles de facturation.
  const [tab, setTab] = React.useState(() => (clean(settings?.legal_name) && clean(settings?.address) ? "billing" : "issuer"));
  const registrationsComplete = Boolean(clean(draft.gst_registration_number) && clean(draft.qst_registration_number));
  const surchargePercent = Number(draft.fuel_surcharge_percent || 0);
  // Valeurs à l'ouverture, puis au dernier enregistrement : l'Aspiration et le dompeur ne partent que s'ils ont changé.
  const savedDraft = React.useRef(draft);
  // Un message sous chaque champ hors règles ; « Enregistrer » attend qu'il n'y en ait plus.
  const issues = issuerSettingsIssues(draft);
  const hasIssues = Object.keys(issues).length > 0;
  const issueText = (field) => issues[field] ? <p role="alert" className="mt-1 text-xs font-semibold text-[#c5524a]">{issues[field]}</p> : null;
  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const saved = await updateIssuerSettings(buildIssuerSettingsPayload({ ...draft, taxes_enabled: registrationsComplete && draft.taxes_enabled }, savedDraft.current));
      // La fenêtre reste ouverte, sur le même onglet : elle suit la nouvelle version.
      savedDraft.current = draft;
      setDraft((current) => ({ ...current, version: saved.version }));
      await onSaved(saved);
    }
    catch (error) { setSaveError(error.message || "Les paramètres n’ont pas pu être enregistrés."); }
    finally { setSaving(false); }
  };
  const sectionTitle = "mb-3 text-xs font-extrabold uppercase tracking-wide text-slate-500";
  return <Modal title="Paramètres" subtitle={tab === "issuer" ? "Votre entreprise, telle qu’elle apparaîtra sur chaque relevé de transport." : "Les règles appliquées au calcul de chaque nouveau relevé de transport."} onClose={onClose} medium>
    <div className="flex shrink-0 gap-1.5 border-b border-slate-200 px-5 py-2" role="tablist" aria-label="Sections des paramètres">{ISSUER_SETTINGS_TABS.map((option) => <button key={option.value} type="button" role="tab" aria-selected={tab === option.value} className={`${STATUS_SUB_NAV_BUTTON} ${tab === option.value ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"}`} onClick={() => setTab(option.value)}>{option.label}{option.value === "issuer" && !issuerComplete ? <AlertTriangle className="ml-1.5" size={13} aria-label="À compléter" /> : null}</button>)}</div>
    <div className="min-h-0 overflow-auto p-5">{tab === "issuer" ? <div className="grid gap-5 lg:grid-cols-[1fr_320px]"><section className="rounded-[4px] border border-slate-200 p-4"><h3 className={sectionTitle}>Émetteur</h3><div className="grid gap-3 sm:grid-cols-2"><Field label="Nom légal" required value={draft.legal_name} disabled={!canEdit} onChange={(value) => setField("legal_name", value)} /><div><Field label="Courriel" type="email" value={draft.email} disabled={!canEdit} onChange={(value) => setField("email", value)} />{issueText("email")}</div><Field label="Téléphone" value={draft.phone} disabled={!canEdit} onChange={(value) => setField("phone", value)} /></div><div className="mt-3"><TextAreaField label="Adresse" required value={draft.address} disabled={!canEdit} onChange={(value) => setField("address", value)} /></div></section>
      <BillingLogoField title="Logo de votre entreprise" logoUrl={settings?.logo_data_url} editable={canEdit} disabled={busy}
        note="Affiché sur le relevé de transport : couverture, mot d’accompagnement, L’essentiel et dos."
        onUpload={(file) => onLogoChanged("upload", file)} onRemove={() => onLogoChanged("delete")} /></div>
      : <div className="space-y-3">
        <details className="group rounded-[4px] border border-slate-200 bg-slate-50 text-xs">
          <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 rounded-[4px] px-3 py-2 focus-visible:outline-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden"><span className="flex items-center gap-1.5 font-bold text-slate-700"><ChevronRight size={14} className="group-open:rotate-90" aria-hidden="true" />Paramètres fixes</span><span className="text-slate-500">CAD · Français (Canada) · PDF Lettre</span></summary>
          <ul className="mx-3 mb-2 grid list-none gap-x-6 gap-y-1 border-t border-slate-200 pt-2 text-slate-600 sm:grid-cols-2"><li>Devise : CAD</li><li>Langue : français (Canada)</li><li>PDF : format Lettre</li><li>Relevé : RT-AAAA-00001</li></ul>
        </details>
        <section className="grid gap-3 border-b border-slate-200 py-3 sm:grid-cols-[180px_minmax(0,1fr)]">
          <h3 className="text-xs font-bold text-slate-700 sm:pt-5">Surtaxe carburant</h3>
          <div className="min-w-0"><div className="grid gap-3 md:grid-cols-[110px_minmax(0,1fr)]"><Field compact label="Taux (%)" type="number" min="0" step="0.01" value={draft.fuel_surcharge_percent} disabled={!canEdit} onChange={(value) => setField("fuel_surcharge_percent", value)} /><p className="self-end pb-0.5 text-xs text-slate-600">{issues.fuel_surcharge_percent ? null : surchargePercent > 0 ? <>Pour 1 000,00 $ de services : <strong className="whitespace-nowrap text-slate-900">+ {formatMoney(surchargePercent * 10, "CAD")}</strong> avant taxes.</> : "0 % : aucune surtaxe."}</p></div>{issueText("fuel_surcharge_percent")}<p className="mt-1.5 text-xs text-slate-500">Ajoutée au sous-total, avant la TPS et la TVQ.</p></div>
        </section>
        <section className="border-b border-slate-200 pb-3">
          <h3 className="mb-3 text-xs font-bold text-slate-700">Tarifs automatiques</h3>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]"><h4 className="text-xs font-bold text-slate-700 sm:pt-5">Aspiration</h4><div className="min-w-0"><div className="grid gap-3 sm:grid-cols-[110px_minmax(0,1fr)]"><Field compact label="Montant fixe ($)" type="number" min="0" step="0.01" value={draft.aspiration_fixed_rate} disabled={!canEdit} onChange={(value) => setField("aspiration_fixed_rate", value)} /><Field compact label="Facturer l’Aspiration" value={draft.aspiration_fixed_rate_scope} disabled={!canEdit} onChange={(value) => setField("aspiration_fixed_rate_scope", value)} options={[{ value: "order", label: "Par commande" }, { value: "stop", label: "Par arrêt, partagé selon le tonnage" }]} /></div>{issueText("aspiration_fixed_rate")}<p className="mt-1.5 text-xs text-slate-500">Montant fixe ajouté aux heures facturées.</p></div></div>
            <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]"><h4 className="text-xs font-bold text-slate-700 sm:pt-5">Dompeur</h4><div className="min-w-0"><div className="max-w-44"><Field compact label="Tarif à la tonne ($/t)" type="number" min="0" step="0.01" value={draft.dompeur_tonne_rate} disabled={!canEdit} onChange={(value) => setField("dompeur_tonne_rate", value)} /></div>{issueText("dompeur_tonne_rate")}<p className="mt-1.5 text-xs text-slate-500">Remplace les heures, inspection comprise ; le supplément voyage et les montants fixes s’ajoutent.</p></div></div>
          </div>
          <details className="group mt-2 rounded-[4px] text-xs sm:ml-48"><summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-[4px] text-slate-700 focus-visible:outline-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden"><ChevronRight size={12} className="group-open:rotate-90" aria-hidden="true" />Règles et exceptions</summary><div className="mt-2 space-y-2 text-slate-500"><p>Aspiration : s’applique aux commandes livrées avec Aspirateur Oui ou Long, ou au produit Aspiration, facturées à leur entreprise. L’« Exception $ fixe » d’une commande remplace ce montant ; 0 $ le désactive.</p><p>Dompeur : le tarif $/t d’une commande, saisi dans le Data Manager ou le relevé, remplace ce tarif. Si le tarif est vide, une commande sans $/t reste bloquée.</p></div></details>
        </section>
        <section className="rounded-[4px] border border-slate-200 bg-slate-50 p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h3 className="text-xs font-bold text-slate-700">Taxes — TPS et TVQ</h3><span className={`rounded-[4px] px-1.5 py-0.5 text-[11px] font-bold ${registrationsComplete && draft.taxes_enabled ? "bg-accent-soft text-accent" : "bg-amber-50 text-amber-800"}`}>{registrationsComplete && draft.taxes_enabled ? "Activées" : "Désactivées"}</span></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0"><h4 className="mb-1.5 text-xs font-bold text-slate-700">TPS</h4><div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2"><Field compact label="Numéro de TPS" placeholder="À compléter" value={draft.gst_registration_number} disabled={!canEdit} onChange={(value) => setField("gst_registration_number", value)} /><Field compact label="Taux TPS (%)" type="number" min="0" step="0.001" value={draft.gst_rate_percent} disabled={!canEdit} onChange={(value) => setField("gst_rate_percent", value)} /></div>{issueText("gst_rate_percent")}</div>
            <div className="min-w-0 border-t border-slate-200 pt-3 sm:border-0 sm:pt-0"><h4 className="mb-1.5 text-xs font-bold text-slate-700">TVQ</h4><div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2"><Field compact label="Numéro de TVQ" placeholder="À compléter" value={draft.qst_registration_number} disabled={!canEdit} onChange={(value) => setField("qst_registration_number", value)} /><Field compact label="Taux TVQ (%)" type="number" min="0" step="0.001" value={draft.qst_rate_percent} disabled={!canEdit} onChange={(value) => setField("qst_rate_percent", value)} /></div>{issueText("qst_rate_percent")}</div>
          </div>
          <label className={`mt-3 flex items-start gap-2 rounded-[4px] border px-3 py-2 text-xs ${registrationsComplete ? "border-[var(--color-border-accent-muted)] bg-accent-soft text-accent" : "border-amber-200 bg-amber-50 text-amber-900"}`}><input className={BILLING_CHECKBOX_CLASS_NAME} type="checkbox" checked={registrationsComplete && draft.taxes_enabled} disabled={!canEdit || !registrationsComplete} onChange={(event) => setField("taxes_enabled", event.target.checked)} /><span><strong>Calculer la TPS et la TVQ</strong><span className="mt-0.5 block">{registrationsComplete ? "Les deux taxes seront calculées séparément sur le sous-total et la surtaxe." : "Saisissez les deux numéros d’inscription pour activer les taxes."}</span></span></label>
        </section>
      </div>}</div>
    {saveError ? <p className="mx-5 mb-3 rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">{saveError}</p> : null}<footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">{canEdit && !issuerComplete ? <p className="mr-auto text-xs font-bold text-amber-800">Saisissez le nom légal et l’adresse dans « Entreprise émettrice ».</p> : tab === "billing" ? <p className="mr-auto text-xs text-slate-500">Les relevés de transport déjà envoyés restent inchangés.</p> : null}<button type="button" className={SECONDARY_BUTTON} disabled={busy || saving} onClick={onClose}>Fermer</button>{canEdit ? <button type="button" className={PRIMARY_BUTTON} disabled={busy || saving || !issuerComplete || hasIssues} onClick={save}>{saving ? <LoaderCircle className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}Enregistrer</button> : null}</footer>
  </Modal>;
}

function InvoiceDetail({ invoice, busy, permissions, onAction, onDirtyChange, onSendBlockChange, guideStarted, sync = {}, intent = "view", view = null }) {
  const returnDetailsFor = useInvoiceReturnDetails();
  const lifecycle = clean(invoiceLifecycleStatus(invoice)).toLowerCase();
  const isDraft = lifecycle === "draft";
  const consolidationRefresh = isDraft && invoice.billing_origin === "consolidation" ? invoice.consolidation_refresh : null;
  const refreshBlocked = Boolean(consolidationRefresh && consolidationRefresh.state !== "current") || sync.deferred || sync.error || sync.updating;
  const needsRevalidation = invoiceNeedsRevalidation(invoice);
  const issueBlockingReasons = invoiceIssueBlockingReasons(invoice);
  const issueChecksPending = invoiceIssueChecksPending(invoice);
  const initialEmail = invoice.billing_email || invoice?.billing_account?.billing_email || "";
  const [recipientEmail, setRecipientEmail] = React.useState(initialEmail);
  const emailChanged = recipientEmail !== initialEmail;
  // Adresse incomplète : signalée sous le champ ; « Enregistrer le brouillon » attend seulement si elle a été modifiée.
  const emailError = (emailChanged || clean(recipientEmail)) && !isCompleteEmail(recipientEmail) ? INCOMPLETE_EMAIL_MESSAGE : "";
  const [paymentTerms, setPaymentTerms] = React.useState(() => invoice?.terms?.kind === "specific_date" ? "specific_date" : `net_${invoice?.terms?.days || 30}`);
  const [specificDueDate, setSpecificDueDate] = React.useState(invoice?.terms?.due_date || "");
  const [clientNote, setClientNote] = React.useState(invoice.client_note || "");
  const [localLines, setLocalLines] = React.useState(invoice?.lines || invoice?.invoice_lines || []);
  const [showAddLine, setShowAddLine] = React.useState(false);
  const [addLineMode, setAddLineMode] = React.useState("");
  const [manualDescription, setManualDescription] = React.useState("");
  const [manualAmount, setManualAmount] = React.useState("");
  // Une fois annulé, le relevé rechargé ne redemande pas l’annulation.
  const [confirmVoid, setConfirmVoid] = React.useState(intent === "void" && lifecycle !== "void");
  const [voidReason, setVoidReason] = React.useState("");
  const [notifyCoop, setNotifyCoop] = React.useState(true);
  // Ouverte depuis le clic droit d'une carte : la confirmation demandée est déjà affichée, on l'amène à l'écran.
  React.useEffect(() => {
    if (intent === "void") requestAnimationFrame(() => document.querySelector("[data-billing-intent-confirm]")?.scrollIntoView({ block: "nearest" }));
  }, [intent]);
  // Après un enregistrement, le détail est recréé (nouvelle version) : « Modifier » et la présentation choisie restent.
  const kept = view?.current?.id === invoice.id ? view.current : null;
  const [showDraftEditor, setShowDraftEditor] = React.useState(intent === "edit" || intent === "delete" || Boolean(kept?.editor));
  // « Supprimer le brouillon » (lien du bas ou clic droit) se confirme dans le pied ; jamais gardé d'une version à l'autre.
  const [deleteRequested, setDeleteRequested] = React.useState(intent === "delete");
  const lines = React.useMemo(() => groupInvoiceLinesForDisplay(localLines, invoice.display_lines), [localLines, invoice.display_lines]);
  const lineGroups = useInvoiceLineGroups(lines, localLines, guideStarted);
  // Relevé : la journée de chaque camion, arrêt par arrêt ; « Par chauffeur » garde la lecture par tournée.
  const [lineMode, setLineMode] = React.useState(kept?.lineMode || "orders");
  React.useEffect(() => { if (view) view.current = { id: invoice.id, editor: showDraftEditor, lineMode }; }, [view, invoice.id, showDraftEditor, lineMode]);
  const byOrder = lineMode === "orders" && lineGroups.enabled;
  const lineModeSwitch = lineGroups.enabled ? <div role="group" aria-label="Présentation des lignes" className="flex">
    {[["orders", "Par trajet"], ["drivers", "Par chauffeur"]].map(([mode, label]) => <button key={mode} type="button" aria-pressed={lineMode === mode} onClick={() => setLineMode(mode)}
      className={`inline-flex h-6 items-center border px-2 text-[11px] font-bold first:rounded-l-[4px] last:rounded-r-[4px] focus-visible:outline-2 focus-visible:outline-accent ${lineMode === mode
        ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"}`}>{label}</button>)}
  </div> : null;
  const gps = useInvoiceGps(lineGroups, Boolean(consolidationRefresh) && permissions.gps);
  const orderLines = <InvoiceOrderLines lines={localLines} currency={invoice.currency_code} day={lineGroups.day} query={lineGroups.query} view={lineGroups} busy={busy} gps={gps}
    canEdit={isDraft && invoice.billing_origin === "consolidation" && Boolean(permissions.orderRates)}
    onApply={async (orderId, rates) => Boolean(await onAction("order-rates", { orderId, rates: { version: invoice.version, ...rates } }))} />;
	const initialLines = groupInvoiceLinesForDisplay(invoice?.lines || invoice?.invoice_lines || [], invoice.display_lines);
	const initialLinesByKey = new Map(initialLines.map((line) => [line.billable_item_id || line.id, line]));
	const lineUpdates = lines.flatMap((line) => {
		const initial = initialLinesByKey.get(line.billable_item_id || line.id);
		if (!initial) return [];
		if (line.is_manual || isManualInvoiceLine(line)) {
			const descriptionChanged = clean(line.description) !== clean(initial.description);
			const amountChanged = Number(line.line_total ?? 0) !== Number(initial.line_total ?? 0);
			if (!descriptionChanged && !amountChanged) return [];
			return [{
				invoice_line_id: line.id,
				...(descriptionChanged ? { description: clean(line.description) } : {}),
				...(amountChanged ? { amount: Number(line.line_total) } : {}),
			}];
		}
		const changed = ["quantity", "unit_rate", "voyage_amount"].some(
			(key) => Number(line[key] ?? 0) !== Number(initial[key] ?? 0),
		);
		if (!changed) return [];
		return [{
			billable_item_id: line.billable_item_id,
			...(line.billing_unit === "tonne" ? {
				tonne_quantity: Number(line.quantity),
				tonne_rate: Number(line.unit_rate),
			} : {}),
			...(line.voyage_amount != null ? { voyage_amount: Number(line.voyage_amount) } : {}),
		}];
	});
	const money = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
	const liveSubtotal = money(lines.reduce((sum, line) => sum + Number(line.line_total || 0), 0));
	const invalidLineValues = liveSubtotal < 0 || lines.some((line) => (
		(line.is_manual || isManualInvoiceLine(line))
			? (!clean(line.description) || !Number.isFinite(Number(line.line_total)))
			: (
				(line.billing_unit === "tonne" && (!(Number(line.quantity) > 0) || Number(line.unit_rate) < 0))
				|| (line.voyage_amount != null && Number(line.voyage_amount) < 0)
			)
	));
	// Même calcul que le serveur : la surtaxe carburant s'ajoute au sous-total, puis est taxée.
	const liveSurcharge = money(liveSubtotal * Number(invoice.fuel_surcharge_percent || 0) / 100);
	const liveGst = money((liveSubtotal + liveSurcharge) * Number(invoice.gst_rate_percent || 0) / 100);
	const liveQst = money((liveSubtotal + liveSurcharge) * Number(invoice.qst_rate_percent || 0) / 100);
	const liveTotal = money(liveSubtotal + liveSurcharge + liveGst + liveQst);
  const documents = invoice?.documents || [];
  const deliveries = invoice?.deliveries || [];
  const initialDelivery = deliveries.find((delivery) => ["invoice", "credit_note"].includes(delivery.delivery_kind) && delivery.status === "sent");
  // « Prévenir la Coop » d’un relevé annulé.
  const cancellationDelivery = deliveries.find((delivery) => delivery.delivery_kind === "cancellation");
  const isManualDelivery = isManualInvoiceDelivery(initialDelivery);
  const sentDocument = documents.find((document) => document.id === initialDelivery?.billing_document_id && document.status === "generated");
	const canNotifyCoop = lifecycle === "sent" && Boolean(permissions.send);
	const cancelDates = localLines.map((line) => line.service_date).filter(Boolean);
	const cancelDays = serviceDaysLabel(cancelDates);
	const cancelSingleDay = new Set(cancelDates).size === 1;
  const draftChanged = isDraft && (
    emailChanged
    || paymentTerms !== (invoice?.terms?.kind === "specific_date" ? "specific_date" : `net_${invoice?.terms?.days || 30}`)
    || specificDueDate !== (invoice?.terms?.due_date || "")
    || clientNote !== (invoice.client_note || "")
		|| lineUpdates.length > 0
  );
  React.useLayoutEffect(() => {
    onDirtyChange(draftChanged || showAddLine);
    return () => onDirtyChange(false);
  }, [draftChanged, showAddLine, onDirtyChange]);
  // « Voir le relevé », « Voir le PDF » et « Envoyer le relevé » sont dans le pied du panneau : ils attendent ce que le détail attend.
  const sendBlock = draftChanged || showAddLine ? "Enregistrez d’abord les changements du brouillon."
    : isDraft && refreshBlocked ? "Le brouillon attend sa mise à jour depuis la Consolidation : voir le détail." : "";
  React.useLayoutEffect(() => {
    onSendBlockChange(sendBlock);
    return () => onSendBlockChange("");
  }, [sendBlock, onSendBlockChange]);
	const updateLineValue = (lineKey, field, value) => {
		setLocalLines((current) => current.map((line) => {
			const matches = (line.billable_item_id && line.billable_item_id === lineKey) || line.id === lineKey;
			if (!matches) return line;
			if (field === "description") return { ...line, description: value };
			if (field === "amount") return { ...line, quantity: 1, unit_rate: value, line_total: money(value) };
			if (line.billing_unit === "tonne" && ["quantity", "unit_rate"].includes(field)) {
				const quantity = field === "quantity" ? value : line.quantity;
				const unitRate = field === "unit_rate" ? value : line.unit_rate;
				return { ...line, quantity, unit_rate: unitRate, line_total: money(Number(quantity) * Number(unitRate)) };
			}
			if (line.billing_unit === "voyage" && field === "voyage_amount") {
				return { ...line, quantity: 1, unit_rate: value, line_total: money(value) };
			}
			return line;
		}));
	};
	const resetDraftChanges = () => {
		setRecipientEmail(initialEmail);
		setPaymentTerms(invoice?.terms?.kind === "specific_date" ? "specific_date" : `net_${invoice?.terms?.days || 30}`);
		setSpecificDueDate(invoice?.terms?.due_date || "");
		setClientNote(invoice.client_note || "");
		setLocalLines(invoice?.lines || invoice?.invoice_lines || []);
	};

  // Courriel non modifié : il n'est pas renvoyé, la note et les lignes libres s'enregistrent seules.
  const saveDraft = async () => onAction("save-draft", {
    ...(emailChanged ? { recipient_email: clean(recipientEmail) } : {}),
    payment_terms: paymentTerms,
    specific_due_date: paymentTerms === "specific_date" ? specificDueDate : null,
    client_note: clean(clientNote) || null,
		line_updates: lineUpdates,
    expected_version: invoice.version,
  });
  const closeAddLine = () => {
    setShowAddLine(false);
    setAddLineMode("");
    setManualDescription("");
    setManualAmount("");
  };
  const removeLine = async (line) => {
    const previous = localLines;
    const isManual = line.is_manual || isManualInvoiceLine(line);
    setLocalLines((current) => current.filter((row) => (isManual ? row.id !== line.id : row.billable_item_id !== line.billable_item_id)));
    const result = isManual
      ? await onAction("remove-manual-line", { lineId: line.id, expectedVersion: invoice.version })
      : await onAction("remove-item", { billableItemId: line.billable_item_id, expectedVersion: invoice.version });
    if (!result) setLocalLines(previous);
  };
  const addManualLine = async () => {
    const result = await onAction("add-manual-line", {
      description: clean(manualDescription),
      amount: Number(manualAmount),
      expected_version: invoice.version,
    });
    if (result) closeAddLine();
  };

  const timeline = [
    { label: "Création du brouillon", value: invoice.created_at, done: true },
    { label: isManualDelivery ? "Remise au client" : "Envoi au client", value: initialDelivery?.sent_at, done: Boolean(initialDelivery?.sent_at),
      pending: lifecycle === "void" ? "Non envoyé" : "En attente" },
    ...(lifecycle === "void" ? [{ label: "Annulation", value: invoice.voided_at, done: Boolean(invoice.voided_at), note: invoice.void_reason }] : []),
  ];

  const draftGuided = isDraft && !showDraftEditor;
  // Jours couverts par le relevé : les prestations hors période retournent dans « À facturer ».
  const periodEditor = isDraft && permissions.drafts && isTransportStatement(invoice) ? <InvoicePeriodEditor from={invoice.period_start} to={invoice.period_end}
    disabled={busy || draftChanged} onApply={(period) => onAction("period", { version: invoice.version, ...period })} /> : null;
  const deleteAsked = isDraft && permissions.drafts && deleteRequested;
  // L9 : la croix d'une ligne libre exige « Gérer les brouillons ».
  const canRemoveLines = isDraft && permissions.drafts;

  // Déplié sous le pied du panneau du relevé : l'état, le total et les boutons d'envoi restent ceux du panneau.
  return <section aria-label="Détail du relevé" className="border-t border-slate-300">
    <div className="p-2">
      {draftGuided || lineUpdates.length ? <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">{draftGuided ? <p className="mr-auto text-xs font-semibold leading-tight text-slate-900"><span className="mr-1.5 text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Période</span>{formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}{periodEditor}</p> : null}{lineUpdates.length ? <p className="ml-auto text-xs font-bold text-accent">Total recalculé en direct <strong className="ml-1 text-base font-extrabold tabular-nums text-slate-900">{formatMoney(liveTotal, invoice.currency_code)}</strong></p> : null}</div> : null}
      {draftGuided ? null : (
      <section className={`mb-2 grid gap-x-4 gap-y-2 rounded-[4px] border border-slate-200 bg-slate-50/40 px-2.5 py-2 text-xs sm:grid-cols-2 lg:grid-cols-3 ${showDraftEditor ? "billing-draft-reveal" : ""}`}>
        <div className="min-w-0 leading-tight"><p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Entreprise facturée</p><p className="font-extrabold text-slate-900">{invoiceAccountLabel(invoice)}</p><p className="whitespace-pre-line text-[11px] text-slate-600">{display(invoice.billing_address || invoice?.billing_account?.billing_address)}</p></div>
        <div className="min-w-0 leading-tight"><p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Client Data Manager</p><p className="font-semibold text-slate-900">{invoiceSourceLabel(invoice)}</p></div>
        <div className="min-w-0 leading-tight"><p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Période facturée</p><p className="font-semibold text-slate-900">{formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}{periodEditor}</p></div>
        <div className="min-w-0 leading-tight"><p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Date d’envoi</p><p className="font-semibold text-slate-900">{isDraft ? "Fixée à l’envoi" : initialDelivery?.sent_at ? formatDate(initialDelivery.sent_at) : `Numéroté le ${formatDate(invoice.invoice_date || invoice.issued_at)}, courriel non parti`}</p></div>
        <div className="min-w-0 leading-tight"><p className="mb-0.5 text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Courriel destinataire</p>{isDraft ? <><input aria-label="Courriel destinataire" className={INVOICE_HEADER_INPUT} type="email" value={recipientEmail} disabled={!permissions.drafts || busy} onChange={(event) => setRecipientEmail(event.target.value)} />{emailError ? <p className="mt-0.5 text-[11px] font-semibold text-[#c5524a]">{emailError}</p> : null}</> : <p className="font-semibold text-slate-800">{display(isManualDelivery ? recipientEmail : (initialDelivery?.recipient_email || recipientEmail))}</p>}</div>
      </section>
      )}

      {isDraft && invoice.billing_origin === "consolidation" ? <InvoiceConsolidationNotice refresh={consolidationRefresh} sync={sync} canManage={permissions.drafts} busy={busy} dirty={draftChanged || showAddLine} buttonClassName={SECONDARY_BUTTON} invoiceId={invoice.id} /> : null}
      {isDraft && invoice.issue_checks_failed ? <div className="mb-2 flex flex-wrap items-center gap-2 rounded-[4px] border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"><span className="mr-auto">La vérification avant envoi n’a pas abouti. L’envoi la refera de toute façon.</span><button type="button" className="font-extrabold text-accent hover:underline" disabled={busy} onClick={() => onAction("recheck-issue")}>Réessayer</button></div> : null}
      {consolidationRefresh ? <InvoiceSendChecks showReasons={needsRevalidation && consolidationRefresh.state === "current"} reasons={issueBlockingReasons} notices={invoice.issue_notices} pending={issueChecksPending} failed={Boolean(invoice.issue_checks_failed)} refresh={consolidationRefresh} lines={localLines} email={recipientEmail} gps={gps} invoiceId={invoice.id} />
        : needsRevalidation ? <InvoiceBlockingReasons reasons={issueBlockingReasons} lines={localLines} invoiceId={invoice.id} /> : invoice.source_changed_since_draft && invoice.billing_origin !== "consolidation" ? <div className="mb-2 rounded-[4px] border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs text-blue-900"><strong>La commande source a été modifiée depuis la création de ce brouillon.</strong> Les valeurs facturées restent inchangées. Toute nouvelle facturation passe par Consolidation. Les prestations historiques sont conservées pour consultation et correction.</div> : null}
      {/* « Preuve GPS » of the chosen tour: right column from 1 280 px, under the lines below. */}
      <div className={gps.enabled && gps.selected ? "xl:grid xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start xl:gap-3" : ""}><div className="min-w-0">
      {draftGuided ? (
        <>
          <InvoiceLinesOverview view={lineGroups} currency={invoice.currency_code} actions={lineModeSwitch} byOrder={byOrder} />
          {byOrder ? orderLines : <div className="overflow-x-auto"><table className="w-full min-w-[800px] text-left text-xs"><thead><tr className="text-[10px] uppercase tracking-wide text-slate-400"><th className="px-2 py-1">Livraison</th><th className="px-2 py-1">Commande</th><th className="px-2 py-1">Client</th><th className="px-2 py-1 text-right">Quantité</th><th className="px-2 py-1 text-right" title="Temps facturé arrondi à la minute. Le montant utilise la durée exacte.">Temps</th><th className="px-2 py-1 text-right">Tarif</th><th className="px-2 py-1 text-right" title="Tarif à la tonne appliqué à cette commande.">$/t</th><th className="px-2 py-1 text-right" title="Montant fixe : supplément voyage ou tarif fixe de la commande, par exemple une Aspiration.">Fixe</th><th className="px-2 py-1 text-right">Montant</th></tr></thead><tbody><InvoiceLineGroupRows view={lineGroups} gps={gps} colSpan={9} currency={invoice.currency_code} renderRow={(line, index) => {
            const orderLabel = invoiceOrderReferenceLabel(line.order_reference);
            const isManual = line.is_manual || isManualInvoiceLine(line);
            const hasTonne = !isManual && line.billing_unit === "tonne" && line.quantity != null;
            const hasRate = !isManual && ["tonne", "heure"].includes(line.billing_unit) && line.unit_rate != null;
        const tonneRate = invoiceTonneRate(line);
            const returnProps = returnDetailsFor(line);
            return <React.Fragment key={line.billable_item_id || line.id || `${line.order_reference}-${index}`}><tr>
              <td className="border-t border-slate-200 px-2 py-1 leading-tight">{formatDate(line.service_date)}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 font-bold leading-tight text-accent">{display(orderLabel)}<InvoiceReturnToggle {...returnProps} />{isManual ? <span className="ml-2 inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-100 px-1.5 text-[10px] font-semibold leading-tight text-slate-600">Manuel</span> : null}</td>
              <td className="border-t border-slate-200 px-2 py-1 leading-tight">{isManual ? display(line.description) : (invoice.billing_origin === "consolidation" ? display(line.client_name) : (invoiceLineServiceLabel(line.description) || "—"))}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{hasTonne ? quantityLabel(line) : (line.tonnage != null ? `${formatQuantityTons(line.tonnage)} t` : "—")}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{invoiceTimeLabel(line)}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{hasRate ? `${formatMoney(line.unit_rate, invoice.currency_code)} / ${line.billing_unit === "heure" ? "h" : billingUnitLabel(line.billing_unit, 1)}` : "—"}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{tonneRate == null ? "—" : formatMoney(tonneRate, invoice.currency_code)}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{!isManual && line.voyage_amount != null ? formatMoney(line.voyage_amount, invoice.currency_code) : "—"}</td>
              <td className={`border-t border-slate-200 px-2 py-1 text-right font-extrabold leading-tight ${Number(line.line_total) < 0 ? "text-red-700" : ""}`}>{formatMoney(line.line_total, invoice.currency_code)}</td>
            </tr><InvoiceReturnDetailsRow {...returnProps} colSpan={9} currency={invoice.currency_code} /></React.Fragment>;
          }} /></tbody></table></div>}
        </>
      ) : (
        <>
      <h3 className="mb-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500">Prestations</h3>
      <InvoiceLinesOverview view={lineGroups} currency={invoice.currency_code} actions={lineModeSwitch} byOrder={byOrder} />
      {byOrder ? orderLines : <div className="overflow-x-auto"><table className="w-full min-w-[800px] text-left text-xs"><thead><tr className="text-[10px] uppercase tracking-wide text-slate-400"><th className="px-2 py-1">Livraison</th><th className="px-2 py-1">Commande</th><th className="px-2 py-1">Client</th><th className="px-2 py-1 text-right">Quantité</th><th className="px-2 py-1 text-right" title="Temps facturé arrondi à la minute. Le montant utilise la durée exacte.">Temps</th><th className="px-2 py-1 text-right">Tarif</th><th className="px-2 py-1 text-right" title="Tarif à la tonne appliqué à cette commande.">$/t</th><th className="px-2 py-1 text-right" title="Montant fixe : supplément voyage ou tarif fixe de la commande, par exemple une Aspiration.">Fixe</th><th className="px-2 py-1 text-right">Montant</th>{canRemoveLines ? <th className="w-10 px-2 py-1"><span className="sr-only">Retirer</span></th> : null}</tr></thead><tbody><InvoiceLineGroupRows view={lineGroups} gps={gps} colSpan={canRemoveLines ? 10 : 9} currency={invoice.currency_code} renderRow={(line, index) => {
        const isManual = line.is_manual || isManualInvoiceLine(line);
        const hasTonne = !isManual && line.billing_unit === "tonne" && line.quantity != null;
        const hasRate = !isManual && ["tonne", "heure"].includes(line.billing_unit) && line.unit_rate != null;
        const tonneRate = invoiceTonneRate(line);
        const calculated = !isManual && invoice.billing_origin === "consolidation";
        const orderLabel = invoiceOrderReferenceLabel(line.order_reference);
        const lineKey = line.billable_item_id || line.id;
        const returnProps = returnDetailsFor(line);
        return <React.Fragment key={lineKey || `${line.order_reference}-${index}`}><tr className={line.is_modified ? "bg-amber-50/80" : ""}>
          <td className="border-t border-slate-200 px-2 py-1 leading-tight">{formatDate(line.service_date)}</td>
			<td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 font-bold leading-tight text-accent">{display(orderLabel)}<InvoiceReturnToggle {...returnProps} />{isManual ? <span className="ml-2 inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-100 px-1.5 text-[10px] font-semibold leading-tight text-slate-600">Manuel</span> : null}{line.is_modified ? <span className="ml-2 whitespace-nowrap text-[10px] font-extrabold text-amber-700">● Modifié</span> : null}</td>
			<td className="border-t border-slate-200 px-2 py-1 leading-tight">{isManual && isDraft && permissions.drafts ? <input aria-label={`Description ${orderLabel}`} className={`${INVOICE_HEADER_INPUT} min-w-[12rem]`} value={line.description || ""} disabled={busy} onChange={(event) => updateLineValue(lineKey, "description", event.target.value)} /> : (isManual ? display(line.description) : (invoice.billing_origin === "consolidation" ? display(line.client_name) : (invoiceLineServiceLabel(line.description) || "—")))}</td>
			<td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{hasTonne ? (isDraft && permissions.drafts && !calculated ? <><input aria-label={`Quantité ${orderLabel}`} className={`${LINE_NUMBER_INPUT} ml-auto`} type="number" min="0.1" step="0.1" value={formatInvoiceLineDecimal(line.quantity)} disabled={busy} onChange={(event) => updateLineValue(line.billable_item_id, "quantity", event.target.value)} />{line.is_modified && Number(line.quantity) !== Number(line.original_quantity) ? <small className="mt-0.5 block text-slate-400 line-through">{formatInvoiceLineDecimal(line.original_quantity)} t</small> : null}</> : quantityLabel(line)) : (line.tonnage != null ? `${formatQuantityTons(line.tonnage)} t` : "—")}</td>
			<td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{invoiceTimeLabel(line)}</td>
			<td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{hasRate ? (hasTonne && isDraft && permissions.drafts && !calculated ? <><input aria-label={`Tarif ${orderLabel}`} className={`${LINE_NUMBER_INPUT} ml-auto`} type="number" min="0" step="0.1" value={formatInvoiceLineDecimal(line.unit_rate)} disabled={busy} onChange={(event) => updateLineValue(line.billable_item_id, "unit_rate", event.target.value)} />{line.is_modified && Number(line.unit_rate) !== Number(line.original_unit_rate) ? <small className="mt-0.5 block text-slate-400 line-through">{formatMoney(line.original_unit_rate, invoice.currency_code)}</small> : null}</> : `${formatMoney(line.unit_rate, invoice.currency_code)} / ${line.billing_unit === "heure" ? "h" : billingUnitLabel(line.billing_unit, 1)}`) : "—"}</td>
              <td className="whitespace-nowrap border-t border-slate-200 px-2 py-1 text-right leading-tight">{tonneRate == null ? "—" : formatMoney(tonneRate, invoice.currency_code)}</td>
			<td className="border-t border-slate-200 px-2 py-1 text-right leading-tight">{!isManual && line.voyage_amount != null ? (isDraft && permissions.drafts && !calculated ? <><input aria-label={`Montant fixe ${orderLabel}`} className={`${LINE_NUMBER_INPUT} ml-auto`} type="number" min="0" step="0.0001" value={line.voyage_amount} disabled={busy} onChange={(event) => updateLineValue(line.billable_item_id, "voyage_amount", event.target.value)} />{line.is_modified && Number(line.voyage_amount) !== Number(line.original_voyage_amount) ? <small className="mt-0.5 block text-slate-400 line-through">{formatMoney(line.original_voyage_amount, invoice.currency_code)}</small> : null}</> : formatMoney(line.voyage_amount, invoice.currency_code)) : "—"}</td>
			<td className={`border-t border-slate-200 px-2 py-1 text-right font-extrabold leading-tight ${Number(line.line_total) < 0 ? "text-red-700" : ""}`}>{isManual && isDraft && permissions.drafts ? <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap"><input aria-label={`Montant ${orderLabel}`} className={`${Number(line.line_total) < 0 ? LINE_NUMBER_INPUT.replace("text-slate-800", "text-red-700").replace("disabled:text-slate-500", "disabled:text-red-700") : LINE_NUMBER_INPUT} ml-auto`} type="number" step="0.01" value={line.line_total} disabled={busy} onChange={(event) => updateLineValue(lineKey, "amount", event.target.value)} /><span aria-hidden="true">$</span></span> : formatMoney(line.line_total, invoice.currency_code)}</td>{canRemoveLines ? <td className="border-t border-slate-200 px-2 py-1"><button type="button" aria-label={`Retirer ${isManual ? "la ligne libre" : `la commande ${orderLabel}`}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-700" disabled={busy || calculated} onClick={() => removeLine(line)}><X size={15} /></button></td> : null}
        </tr><InvoiceReturnDetailsRow {...returnProps} colSpan={canRemoveLines ? 10 : 9} currency={invoice.currency_code} /></React.Fragment>;
      }} /></tbody></table></div>}
      {isDraft && permissions.drafts ? <button type="button" className="billing-draft-reveal mt-2 cursor-pointer rounded-[4px] px-1 py-1 text-xs font-extrabold text-accent hover:underline" disabled={busy} onClick={() => { setShowAddLine(true); setAddLineMode("manual"); }}><Plus className="mr-1 inline" size={13} />Ajouter une ligne</button> : null}
      {showAddLine && addLineMode === "manual" ? <section className="mt-2 rounded-[8px] border border-slate-200 bg-slate-50 p-3 text-xs"><div className="flex items-center justify-between"><strong>Ligne libre</strong><button type="button" onClick={closeAddLine}><X size={15} /></button></div><div className="mt-2 grid gap-2 sm:grid-cols-[1fr_8rem]"><Field label="Description" required value={manualDescription} disabled={busy} onChange={setManualDescription} placeholder="Ex. Rabais volume" /><Field label="Montant" required type="number" step="0.01" value={manualAmount} disabled={busy} onChange={setManualAmount} /></div><p className="mt-1 text-[11px] font-normal text-slate-500">Un montant négatif accorde un rabais. Il ne peut pas dépasser le sous-total.</p><div className="mt-2 flex justify-end"><button type="button" className={`${PRIMARY_BUTTON} h-8 text-xs`} disabled={busy || !clean(manualDescription) || manualAmount === "" || !Number.isFinite(Number(manualAmount)) || money(liveSubtotal + Number(manualAmount)) < 0} onClick={addManualLine}>Ajouter la ligne</button></div></section> : null}
        </>
      )}
      </div>{gps.enabled && gps.selected ? <InvoiceGpsPanel gps={gps} /> : null}</div>

      {isDraft ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <button type="button" className="px-1 py-1 text-xs font-extrabold text-accent hover:underline" disabled={busy} onClick={() => setShowDraftEditor((current) => !current)}>
            {showDraftEditor ? "Masquer" : "Modifier"}
          </button>
          {showDraftEditor && permissions.drafts ? <button type="button" className="px-1 py-1 text-xs font-bold text-red-700 hover:underline" disabled={busy} onClick={() => setDeleteRequested(true)}><Trash2 className="mr-1 inline" size={13} />Supprimer le brouillon</button> : null}
        </div>
      ) : null}
      {!draftGuided ? <section className={`mt-3 rounded-[4px] ${showDraftEditor ? "billing-draft-reveal" : ""}`}><TextAreaField label="Note pour le client" value={clientNote} disabled={!isDraft || !permissions.drafts} onChange={setClientNote} compact placeholder="Optionnel — ajoutée au courriel d’envoi du relevé." /></section> : null}
      {initialDelivery ? <section className="mt-3 rounded-[8px] border border-slate-200 p-2.5 text-xs"><strong>{isManualDelivery ? "Remise confirmée" : "Envoi confirmé"}</strong><p className="mt-1">{isManualDelivery ? `Remise au client (sans courriel) le ${formatDate(initialDelivery.sent_at, true)}.` : `Envoyé à ${initialDelivery.recipient_email} le ${formatDate(initialDelivery.sent_at, true)}.`}</p>{cancellationDelivery ? <p className="mt-1 text-slate-600">{cancellationDelivery.status === "sent" ? `Annulation signalée à ${cancellationDelivery.recipient_email} le ${formatDate(cancellationDelivery.sent_at, true)}.` : cancellationDelivery.status === "failed" && cancellationDelivery.retry_planned ? "Le courriel d’annulation n’est pas encore parti : nouvel essai prévu."
        : cancellationDelivery.status === "failed" || cancellationDelivery.status === "dead_letter" ? "Le courriel d’annulation n’a pas pu partir." : "Courriel d’annulation en file d’envoi."}</p> : null}{sentDocument ? <button type="button" className={`${SECONDARY_BUTTON} mt-2 h-8 text-xs`} onClick={() => onAction("download-sent", { documentId: sentDocument.id })}><Download size={13} />{isManualDelivery ? "PDF remis" : "PDF envoyé"}</button> : null}</section> : null}
      {!isDraft ? <section className="mt-3"><h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Cycle de vie</h3><ol className="mt-1.5 flex w-full min-w-0 gap-0 overflow-x-auto">{timeline.map((event, index) => <li key={event.label} className="relative flex min-w-[7.5rem] flex-1 flex-col items-start pr-3 text-xs sm:min-w-0">{index < timeline.length - 1 ? <span className="absolute left-4 right-0 top-2 h-px bg-slate-200" aria-hidden="true" /> : null}<span className={`relative z-10 grid h-4 w-4 place-items-center rounded-full border text-[9px] ${event.done ? "border-accent bg-accent text-white" : "border-slate-400 bg-white text-transparent"}`}>{event.done ? "✓" : ""}</span><div className={`mt-1.5 leading-tight ${event.done ? "text-slate-900" : "text-slate-400"}`}><strong className="block">{event.label}</strong><p className="mt-0.5">{event.value ? formatDate(event.value, true) : event.pending}</p>{event.note ? <p className="mt-0.5 break-words text-slate-600">Motif : {event.note}</p> : null}</div></li>)}</ol></section> : null}
      {confirmVoid ? <div data-billing-intent-confirm className="mt-5 rounded-[4px] border border-slate-300 border-l-4 border-l-[#c5524a] bg-white p-4">
        <h3 className="font-extrabold text-slate-900">Annuler le relevé {invoice.invoice_number}</h3>
        <ol className="mt-3 space-y-2 text-xs text-slate-700">
          <li><strong>1.</strong> Ses prestations retournent dans « À facturer ».{cancelDays ? (cancelSingleDay ? ` La journée ${cancelDays} peut être rouverte dans Consolidation.` : ` Les journées ${cancelDays} peuvent être rouvertes dans Consolidation.`) : ""}</li>
          <li><strong>2.</strong> {invoice.invoice_number} reste visible, barré, dans « Terminés ».{lifecycle === "sent" ? " Son lien en ligne affichera « Relevé annulé »." : ""}</li>
          <li><strong>3.</strong> Le relevé refait prendra un nouveau numéro.</li>
        </ol>
        <div className="mt-3"><TextAreaField label="Motif de l’annulation" required value={voidReason} onChange={setVoidReason} placeholder="Ex. Heures du 7 oct. à corriger…" /></div>
        <p className="mt-1 text-[11px] text-slate-500">Le motif reste dans l’historique du relevé. Il n’est pas envoyé à l’entreprise.</p>
        {canNotifyCoop ? <>
          <label className="mt-3 flex items-start gap-2 text-xs text-slate-800"><input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]" checked={notifyCoop} onChange={(event) => setNotifyCoop(event.target.checked)} /><span><strong>Prévenir l’entreprise par courriel</strong>{invoice.billing_email ? ` · ${invoice.billing_email}` : ""}</span></label>
          {notifyCoop && invoice.cancellation_preview ? <div className="ml-6 mt-2 rounded-[4px] border border-slate-200 bg-white p-3 text-xs text-slate-700"><p><strong>Objet :</strong> {invoice.cancellation_preview.subject}</p><p className="mt-1 whitespace-pre-line">{invoice.cancellation_preview.text}</p></div> : null}
        </> : null}
        <div className="mt-3 flex justify-end gap-2"><button type="button" className={SECONDARY_BUTTON} onClick={() => setConfirmVoid(false)}>Retour</button><button type="button" className={CANCEL_CONFIRM_BUTTON} disabled={clean(voidReason).length < 3 || busy} onClick={() => onAction("void", { reason: clean(voidReason), expected_version: invoice.version, notify_client: canNotifyCoop && notifyCoop })}>Annuler le relevé</button></div>
      </div> : null}
    </div>
    {/* Changements à enregistrer ou suppression demandée au clic droit : le reste est dans le pied du panneau. */}
    {!confirmVoid && (draftChanged || deleteAsked) ? <footer className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-2 py-1.5">{deleteAsked ? <span className="mr-auto text-xs text-slate-600"><strong>Supprimer ce brouillon ?</strong> Ses prestations retournent dans « À facturer » ; ses lignes libres et sa note sont perdues.</span>
      : draftChanged ? <span className="mr-auto text-xs text-slate-600"><strong>{lineUpdates.length ? `${lineUpdates.length} ligne${lineUpdates.length > 1 ? "s" : ""} modifiée${lineUpdates.length > 1 ? "s" : ""}` : "Brouillon modifié"}</strong> — changements non enregistrés</span> : null}{permissions.drafts && draftChanged ? <><button type="button" className={SECONDARY_BUTTON} disabled={busy} onClick={resetDraftChanges}>Annuler les changements</button><button type="button" className={PRIMARY_BUTTON} disabled={busy || invalidLineValues || (emailChanged && Boolean(emailError))}
      title={emailChanged && emailError ? emailError : undefined} onClick={() => saveDraft()}>Enregistrer le brouillon</button></> : null}{deleteAsked ? <button type="button" className={DANGER_BUTTON} disabled={busy} onClick={() => onAction("delete")}><Trash2 size={14} />Supprimer</button> : null}</footer> : null}
  </section>;
}

function BillingHomeView({
  issuer,
  accounts = [],
  canEdit = false,
  busy = false,
  onOpenIssuer,
  onAddAccount,
  onOpenDirectory,
  onContinue,
}) {
  const hasIssuer = Boolean(clean(issuer?.legal_name) && clean(issuer?.address));
  const hasAccount = (Array.isArray(accounts) ? accounts : []).some((account) => account?.id && account.is_active !== false);
  const ready = hasIssuer && hasAccount;
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-3 pb-6 pt-2 text-slate-800">
      <WorkspacePageHeader truckBanner icon={FileText} title="Facturation" description="Renseignez vos coordonnées et créez une entreprise de facturation pour commencer." className="shrink-0" />
      <section className="shrink-0 rounded-[4px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="max-w-2xl">
          <h2 className="text-lg font-extrabold text-slate-900">Premiers pas</h2>
          <p className="mt-1 text-sm text-slate-600">Trois étapes, puis vous verrez les prestations prêtes à facturer.</p>
          <ol className="mt-4 grid gap-3 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <span className={STEP_BADGE}>{hasIssuer ? <CheckCircle2 size={14} /> : "1"}</span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-slate-900">Entreprise émettrice</p>
                <p className="mt-0.5 text-slate-600">Nom légal et adresse de votre entreprise, en tête de chaque relevé de transport.</p>
                <button type="button" className={`${SECONDARY_BUTTON} mt-2 h-9 px-3 text-xs`} disabled={busy} onClick={onOpenIssuer}>
                  <UserRound size={13} />
                  {hasIssuer ? "Modifier" : "Renseigner"}
                </button>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className={STEP_BADGE}>{hasAccount ? <CheckCircle2 size={14} /> : "2"}</span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-slate-900">Ajouter une entreprise de facturation</p>
                <p className="mt-0.5 text-slate-600">L’entreprise qui reçoit les relevés de transport : son courriel, son tarif horaire et ses suppléments éventuels.</p>
                <button type="button" className={`${SECONDARY_BUTTON} mt-2 h-9 px-3 text-xs`} disabled={busy || !canEdit} onClick={onAddAccount}>
                  <Plus size={13} />
                  {hasAccount ? "Ajouter une autre" : "Ajouter"}
                </button>
                <button type="button" className={`${SECONDARY_BUTTON} ml-2 mt-2 h-9 rounded-[4px] px-3 text-xs`} disabled={busy} onClick={onOpenDirectory}>Voir les clients à rattacher</button>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className={STEP_BADGE}>3</span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-slate-900">Voir les prestations prêtes</p>
                <p className="mt-0.5 text-slate-600">Validez les relevés et préchargements dans Consolidation, puis créez un brouillon par payeur et période.</p>
                <button type="button" className={`${PRIMARY_BUTTON} mt-2 h-9 px-3 text-xs`} disabled={busy || !ready} onClick={onContinue}>
                  <FileText size={13} />
                  Voir les prestations prêtes
                </button>
              </div>
            </li>
          </ol>
        </div>
      </section>
    </div>
  );
}

const EMPTY_RUNTIME_FILTERS = Object.freeze({
  search: "",
  clientId: "",
  clientName: "",
  billingAccountId: "",
  billingSide: "",
  fromDate: "",
  toDate: "",
  status: "",
  blockingReason: "",
  overdueOnly: false,
});

function resolveBillingTabFilters(preferences, activeTab, cursor = "") {
  const profile = activeTab === "invoices"
    ? preferences?.invoiceFilters
    : activeTab === "billable"
      ? preferences?.billableFilters
      : preferences?.accountsFilters;
  return {
    ...EMPTY_RUNTIME_FILTERS,
    ...(profile || {}),
    cursor,
    limit: 10000,
  };
}

// Sans « Consulter la facturation » : le routeur affiche cette page au lieu de renvoyer à l'accueil sans rien dire.
export function AccessDenied() {
  return <div className="grid h-full min-h-96 place-items-center p-6 text-center"><div><AlertTriangle className="mx-auto mb-2 text-slate-500" size={26} /><h2 className="font-extrabold text-slate-800">Accès à la facturation requis</h2><p className="mt-1 text-sm text-slate-600">Il faut le droit « Consulter la facturation ». Demandez-le à un administrateur.</p></div></div>;
}

export default function BillingPage() {
  const { hasPermission } = useAccess();
  const { showToast, updateToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = searchParams.get("tab");
  const explicitTab = TABS.some((tab) => tab.key === requestedTab) ? requestedTab : "";
  const handlePreferenceError = React.useCallback((preferenceError, meta = {}) => {
    const operationLabel = meta.operation === "load" ? "chargées" : "enregistrées";
    showToast({
      title: `Préférences non ${operationLabel}`,
      message: preferenceError?.message || "Les préférences de facturation sont temporairement indisponibles.",
      type: "error",
      duration: 7000,
    });
  }, [showToast]);
  const {
    isLoading: preferencesLoading,
    preferences: billingPreferences,
    updatePreferences: updateBillingPreferences,
  } = useBillingPreferences({ onError: handlePreferenceError });
  // Un onglet mémorisé qui n'existe plus (Rapports, Export) retombe sur « À facturer ».
  const savedTab = TABS.some((tab) => tab.key === billingPreferences?.lastTab) ? billingPreferences.lastTab : "";
  const activeTab = explicitTab || savedTab || "billable";
  const [cursorByTab, setCursorByTab] = React.useState({ billable: "", invoices: "" });
  const activeFilterProfile = activeTab === "invoices"
    ? billingPreferences?.invoiceFilters
    : activeTab === "billable"
      ? billingPreferences?.billableFilters
      : billingPreferences?.accountsFilters;
  const activeFilterSignature = JSON.stringify(activeTab === "accounts"
    ? {} : activeFilterProfile || {});
  const filters = React.useMemo(
    () => resolveBillingTabFilters(billingPreferences, activeTab, cursorByTab[activeTab] || ""),
    [activeFilterSignature, activeTab, cursorByTab],
  );
  const preferencesReady = !preferencesLoading && Boolean(billingPreferences);
  const associationsGuardRef = React.useRef(null);
  const referencesLoadSequenceRef = React.useRef(0);
  const onAssociationsGuardChange = React.useCallback((guard) => { associationsGuardRef.current = guard; }, []);
  const [accounts, setAccounts] = React.useState([]);
  const [sourceClients, setSourceClients] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [billableKpis, setBillableKpis] = React.useState(() => billableKpisFromSummary());
	const [invoiceSummary, setInvoiceSummary] = React.useState({ status_counts: {}, overdue: {}, outstanding: {}, due_next_7_days: {}, drafts: {} });
  const [meta, setMeta] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());
  const [preview, setPreview] = React.useState(null);
  const [accountEditor, setAccountEditor] = React.useState(null);
  const [issuer, setIssuer] = React.useState(null);
  const [issuerSnapshot, setIssuerSnapshot] = React.useState(null);
  const [issuerSnapshotReady, setIssuerSnapshotReady] = React.useState(false);
  const [referencesReady, setReferencesReady] = React.useState(false);
  const [setupHomeLocked, setSetupHomeLocked] = React.useState(null);
  const [invoiceDetail, setInvoiceDetail] = React.useState(null);
  const [invoiceSummaryId, setInvoiceSummaryId] = React.useState(null);
  React.useEffect(() => { setInvoiceSummaryId(null); }, [activeTab, filters]);
  const [invoiceDirty, setInvoiceDirty] = React.useState(false);
  const [detailSendBlock, setDetailSendBlock] = React.useState("");
  // Autre courriel saisi dans « Envoyer le relevé » du panneau : la mise à jour du détail déplié attend, comme pour une saisie du brouillon.
  const [sendEmailDirty, setSendEmailDirty] = React.useState(false);
  const detailGuideStarted = React.useRef(false);
  const detailView = React.useRef({ id: "", editor: false, lineMode: "orders" });
  // Brouillon modifié dans le détail : changer de relevé, replier le détail ou d'onglet abandonne ces changements.
  const leaveDetail = async () => !invoiceDirty || confirmInGrain("Les changements de ce brouillon ne sont pas enregistrés. Les abandonner ?", { title: "Abandonner les changements ?", confirmLabel: "Abandonner" });
	const [invoiceIntent, setInvoiceIntent] = React.useState("view");
  const loadRequestGuardRef = React.useRef(null);
  const activeTabRef = React.useRef(activeTab);
  const referencesLoadedRef = React.useRef(false);
  if (!loadRequestGuardRef.current) loadRequestGuardRef.current = createBillingLoadRequestGuard();
  activeTabRef.current = activeTab;
  // Invoice reads defer the slow Consolidation check; it completes here in the background.
  const issueChecksRef = React.useRef(null);
  if (!issueChecksRef.current) {
    issueChecksRef.current = createInvoiceIssueChecksCoordinator({
      fetchChecks: (invoiceIds) => fetchInvoiceIssueChecks(invoiceIds),
      revision: readOrderDataRevision,
      onResults: (results) => {
        setRows((current) => mergeInvoiceIssueCheckResults(current, results));
        setInvoiceDetail((current) => (current ? mergeInvoiceIssueCheckResults([current], results)[0] : current));
      },
    });
  }

  const permissions = {
    drafts: hasPermission(ACCESS_PERMISSION_KEYS.billingDraftsManage),
    send: hasPermission(ACCESS_PERMISSION_KEYS.billingSend),
    void: hasPermission(ACCESS_PERMISSION_KEYS.billingVoid),
    settings: hasPermission(ACCESS_PERMISSION_KEYS.billingSettingsManage),
    // Same rights as the Consolidation Geotab day read by « Preuve GPS ».
    // Changer un tarif rouvre et revalide le relevé Consolidation de la commande.
    orderRates: hasPermission(ACCESS_PERMISSION_KEYS.billingDraftsManage) && hasPermission(ACCESS_PERMISSION_KEYS.consolidationWrite)
      && hasPermission(ACCESS_PERMISSION_KEYS.consolidationReopen) && hasPermission(ACCESS_PERMISSION_KEYS.consolidationValidate),
    gps: hasPermission(ACCESS_PERMISSION_KEYS.consolidationRead) && (hasPermission(ACCESS_PERMISSION_KEYS.consolidationGeotabRead) || hasPermission(ACCESS_PERMISSION_KEYS.geotabRead)),
  };

  const loadReferences = React.useCallback(async (options = {}) => {
    const sequence = ++referencesLoadSequenceRef.current;
    const [accountPayload, clientPayload] = await Promise.all([
      fetchBillingAccounts(options),
      fetchSourceClients(options),
    ]);
    if (options.signal?.aborted) throw new DOMException("Lecture obsolète", "AbortError");
    const next = { accounts: asItems(accountPayload), sourceClients: asItems(clientPayload) };
    // Doublée par une lecture plus récente (ex. retour du focus) : n'écrase pas le répertoire, mais la fiche ouverte reçoit ses données.
    if (sequence !== referencesLoadSequenceRef.current) return next;
    setAccounts(next.accounts);
    setSourceClients(next.sourceClients);
    referencesLoadedRef.current = true;
    setReferencesReady(true);
    return next;
  }, []);

  const loadView = React.useCallback(async ({ silent = false } = {}) => {
    const tabAtStart = activeTab;
    if (!preferencesReady) return;
    const guard = loadRequestGuardRef.current;
    const { requestId, signal } = guard.begin();
    silent ? setRefreshing(true) : setLoading(true);
    setError("");
    const isStale = () => signal.aborted || !guard.isCurrent(requestId) || activeTabRef.current !== tabAtStart;
    try {
      if (tabAtStart === "accounts") {
        await loadReferences({ signal });
        if (isStale()) return;
        setRows([]);
        setBillableKpis(billableKpisFromSummary());
				setInvoiceSummary({ status_counts: {}, overdue: {}, outstanding: {}, due_next_7_days: {}, drafts: {} });
        setMeta({});
      } else {
        const summaryFilters = {
          search: filters.search,
          clientId: filters.clientId,
          clientName: filters.clientName,
          billingAccountId: filters.billingAccountId,
          billingSide: filters.billingSide,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
        };
        // These reads use the restored filters and do not depend on each other.
        const [payload, summary] = await Promise.all([
          tabAtStart === "invoices" ? fetchInvoices({ ...filters, status: "" }, { signal }) : fetchBillableItems(filters, { signal }),
          tabAtStart === "invoices" ? fetchInvoiceSummary(summaryFilters, { signal }) : fetchBillableItemSummary(summaryFilters, { signal }),
          referencesLoadedRef.current ? undefined : loadReferences({ signal }),
        ]);
        if (isStale()) return;
        const tableItems = asItems(payload);
        setRows(tableItems);
        setMeta(payload?.meta || {});
        if (tabAtStart === "billable") {
          setBillableKpis(billableKpisFromSummary(summary));
        } else {
          setBillableKpis(billableKpisFromSummary());
					setInvoiceSummary(summary || { status_counts: {}, overdue: {}, outstanding: {}, due_next_7_days: {}, drafts: {} });
        }
      }
    } catch (loadError) {
      if (isStale() || loadError?.name === "AbortError") return;
      setError(loadError.message || "Impossible de charger la facturation.");
    } finally {
      if (!signal.aborted && guard.isCurrent(requestId)) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [activeTab, filters, loadReferences, preferencesReady]);

  const invoiceSync = useBillingLiveRefresh({
    enabled: preferencesReady && !busy,
    dirty: invoiceDirty || sendEmailDirty,
    canManageDrafts: permissions.drafts,
    invoice: invoiceDetail,
    setInvoice: setInvoiceDetail,
    refreshView: () => {
      referencesLoadedRef.current = false;
      return loadView({ silent: true });
    },
    onError: (failure) => setError(failure.message || "Impossible d’actualiser le relevé ouvert."),
  });

  React.useEffect(() => () => {
    loadRequestGuardRef.current?.dispose();
    issueChecksRef.current?.reset();
    referencesLoadSequenceRef.current += 1;
  }, []);

  // Layout effect: a reused check result replaces « Vérification… » before paint.
  React.useLayoutEffect(() => {
    issueChecksRef.current?.sync(invoiceDetail ? [invoiceDetail, ...rows] : rows);
  }, [rows, invoiceDetail]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await fetchIssuerSettings();
        if (!cancelled) setIssuerSnapshot(settings || {});
      } catch {
        if (!cancelled) setIssuerSnapshot({});
      } finally {
        if (!cancelled) setIssuerSnapshotReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!preferencesReady) return undefined;
    const timer = setTimeout(() => loadView(), filters.search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [loadView, filters.search, preferencesReady]);

  React.useEffect(() => { setSelected(new Set()); }, [activeTab]);

  React.useEffect(() => {
    if (preferencesLoading || !billingPreferences || !explicitTab || billingPreferences.lastTab === explicitTab) return;
    updateBillingPreferences((current) => ({ ...current, lastTab: explicitTab }));
  }, [billingPreferences, explicitTab, preferencesLoading, updateBillingPreferences]);

  const changeTab = async (key) => {
    if (key === activeTab) return;
    if (associationsGuardRef.current && !await associationsGuardRef.current()) return;
    if (!await leaveDetail()) return;
    setAccountEditor(null);
    loadRequestGuardRef.current?.invalidate();
    setRows([]);
    setBillableKpis(billableKpisFromSummary());
		setInvoiceSummary({ status_counts: {}, overdue: {}, outstanding: {}, due_next_7_days: {}, drafts: {} });
    setMeta({});
    setLoading(true);
    setRefreshing(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", key);
    nextParams.delete("invoice_id");
    setSearchParams(nextParams);
    setCursorByTab((current) => ({ ...current, [key]: "" }));
    updateBillingPreferences((current) => ({ ...current, lastTab: key }));
    setSelected(new Set());
    setError("");
  };
  const resetFilters = () => {
    setCursorByTab((current) => ({ ...current, [activeTab]: "" }));
    updateBillingPreferences((current) => {
      if (activeTab === "invoices") {
        return { ...current, invoiceFilters: createDefaultInvoiceFilters() };
      }
      if (activeTab === "accounts") {
        return {
          ...current,
          accountsFilters: {
            ...createDefaultAccountsFilters(),
            subTab: "accounts",
          },
        };
      }
      return { ...current, billableFilters: createDefaultBillableFilters() };
    }, { immediate: true }).catch(() => {});
    setSelected(new Set());
  };
  // key : un filtre, ou plusieurs à la fois ({ fromDate, toDate }).
  const updateFilter = (key, value) => {
    if (key === "cursor") {
      setCursorByTab((current) => ({ ...current, [activeTab]: value }));
      setSelected(new Set());
      return;
    }
    setCursorByTab((current) => ({ ...current, [activeTab]: "" }));
    updateBillingPreferences((current) => {
      const profileKey = activeTab === "invoices" ? "invoiceFilters" : "billableFilters";
      const changes = typeof key === "object" ? key : { [key]: value };
      return {
        ...current,
        [profileKey]: {
          ...current[profileKey],
          ...changes,
          ...(key === "status" && profileKey === "billableFilters"
            ? {
                overdueOnly: false,
                blockingReason: value === "blocked" ? APPROVED_BLOCKED_FILTER_CODE : "",
              }
            : {}),
        },
      };
    });
    setSelected(new Set());
  };
  const applyBillableKpiFilter = ({ status = "", overdueOnly = false } = {}) => {
    setCursorByTab((current) => ({ ...current, billable: "" }));
    updateBillingPreferences((current) => ({
      ...current,
      billableFilters: {
        ...current.billableFilters,
        status,
        overdueOnly: Boolean(overdueOnly),
        blockingReason: status === "blocked" ? APPROVED_BLOCKED_FILTER_CODE : "",
      },
    }));
    setSelected(new Set());
  };
  const applyBlockingReasonFilter = (code) => {
    const nextCode = clean(code);
    setCursorByTab((current) => ({ ...current, billable: "" }));
    updateBillingPreferences((current) => {
      const sameReason = clean(current.billableFilters.blockingReason) === nextCode;
      return {
        ...current,
        billableFilters: {
          ...current.billableFilters,
          status: "blocked",
          blockingReason: sameReason ? "" : nextCode,
          overdueOnly: false,
        },
      };
    });
    setSelected(new Set());
  };
  const updateAccountsSearch = (search) => {
    updateBillingPreferences((current) => ({
      ...current,
      accountsFilters: { ...current.accountsFilters, search },
    }));
  };
  const updateDirectoryStatus = (directoryStatus) => updateBillingPreferences((current) => ({
    ...current, accountsFilters: { ...current.accountsFilters, directoryStatus },
  }));

  const notifyError = (title, actionError) => showToast({ title, message: actionError.message || "Une erreur est survenue.", type: "error", duration: 8000 });
  const run = async (operation, successMessage, after, pendingTitle = "") => {
    setBusy(true);
    const toastId = pendingTitle ? showToast({ title: pendingTitle, message: "Veuillez patienter pendant la préparation de l’envoi.", type: "loading", duration: null }) : null;
    try {
      const result = await operation();
      const successToast = { title: successMessage, type: "success", duration: 4500 };
      if (toastId) updateToast(toastId, { ...successToast, message: "" });
      else showToast(successToast);
      await (after ? after(result) : loadView({ silent: true }));
      return result;
    } catch (actionError) {
      if (toastId) updateToast(toastId, { title: "Action impossible", message: actionError.message || "Une erreur est survenue.", type: "error", duration: 8000 });
      else notifyError("Action impossible", actionError);
      if (invoiceDetail && invoiceDirty) invoiceSync.reportConflict(actionError);
      return null;
    } finally { setBusy(false); }
  };

  const openIssuer = async () => {
    setBusy(true);
    try {
      const settings = await fetchIssuerSettings();
      setIssuerSnapshot(settings || {});
      setIssuer(settings);
    }
    catch (actionError) { notifyError("Les paramètres ne peuvent pas être chargés", actionError); }
    finally { setBusy(false); }
  };
  // Lien « Paramètres » d'un dompeur sans tarif à la tonne (« À facturer », Consolidation) : ouvre la fenêtre, onglet « Facturation ».
  React.useEffect(() => {
    if (!searchParams.has("parametres")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("parametres");
    setSearchParams(nextParams, { replace: true, state: location.state });
    void openIssuer();
  }, [searchParams]);
  // Le détail d'un relevé se déplie dans son panneau : l'adresse le garde (retour depuis Consolidation), sans page de plus dans l'historique.
  const openInvoice = async (invoiceId, intent = "view") => {
    if (!await leaveDetail()) return;
    // La flèche des journées repliées passe une fois par ouverture du détail : pas en suivant un autre relevé, ni sur une confirmation.
    if (!searchParams.has("invoice_id") || intent !== "view") detailGuideStarted.current = intent !== "view";
    setInvoiceSummaryId(invoiceId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("invoice_id", invoiceId);
    setSearchParams(nextParams, { replace: true, state: { ...location.state, invoiceIntent: intent,
      invoiceRequest: intent === "view" ? location.state?.invoiceRequest : Date.now() } });
  };
  // `force` : le brouillon vient d'être supprimé, il n'y a plus rien à garder.
  const closeInvoice = async (force = false) => {
    if (force !== true && !await leaveDetail()) return;
    detailView.current = { id: "", editor: false, lineMode: "orders" };
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("invoice_id");
    setSearchParams(nextParams, { replace: true, state: location.state });
  };
  // Clic droit sur les cartes : PDF tout de suite ; envoi pour une ou plusieurs cartes.
  const cardAction = async (action, invoices, payload = {}) => {
    // Envoi depuis le panneau : le détail déplié montre ensuite le relevé numéroté ou envoyé.
    const afterSend = async (result) => {
      if (invoiceDetail && invoices.some((invoice) => invoice.id === invoiceDetail.id)) setInvoiceDetail(result?.id === invoiceDetail.id ? result : await fetchInvoice(invoiceDetail.id));
      void loadView({ silent: true });
    };
    const count = invoices.length;
    if (action === "excel") return run(async () => { for (const invoice of invoices) await downloadInvoiceXlsx(invoice.id); }, "Excel du relevé téléchargé", async () => {});
    if (action === "download" || action === "preview-pdf") {
      return run(async () => { for (const invoice of invoices) await downloadInvoicePdf(invoice.id, { filename: `${invoice.invoice_number || "brouillon"}.pdf`, preview: action === "preview-pdf" }); },
        action === "preview-pdf" ? "Aperçu PDF ouvert" : count > 1 ? `${count} PDF téléchargés` : "PDF téléchargé", async () => {});
    }
    // « Envoyer » d'une carte : un autre courriel est d'abord enregistré sur le brouillon, comme depuis le détail du relevé.
    if (action === "send-statement") {
      const [invoice] = invoices;
      return run(async () => {
        let version = invoice.version;
        if (payload.recipient_email !== clean(invoice.billing_email)) {
          const draft = await fetchInvoice(invoice.id);
          const specific = draft.terms?.kind === "specific_date";
          ({ version } = await updateDraftInvoice(invoice.id, { recipient_email: payload.recipient_email,
            payment_terms: specific ? "specific_date" : `net_${draft.terms?.days || 30}`, specific_due_date: specific ? draft.terms.due_date : null,
            client_note: clean(draft.client_note) || null, line_updates: [], expected_version: invoice.version }));
        }
        return issueInvoice(invoice.id, { expected_version: version, send_after_issue: true });
      }, "Relevé numéroté ; le courriel partira avec le PDF", afterSend, "Préparation et envoi du relevé…");
    }
    if (action !== "send") return null;
    const refused = [];
    setBusy(true);
    try {
      for (const invoice of invoices) {
        try { await sendInvoice(invoice.id, payload); }
        catch (sendError) { refused.push({ number: invoice.invoice_number || "Brouillon", message: sendError.message || "Une erreur est survenue." }); }
      }
    } finally { setBusy(false); }
    const launched = count - refused.length;
    const title = `${launched} envoi${launched > 1 ? "s" : ""} lancé${launched > 1 ? "s" : ""}`;
    const reasons = refused.map(({ number, message }) => (count > 1 ? `${number} : ${message}` : message)).join("\n");
    if (!refused.length) showToast({ title, type: "success", duration: 4500 });
    else if (!launched) showToast({ title: "Action impossible", message: reasons, type: "error", duration: 8000 });
    else showToast({ title: `${title} · ${refused.length} refusé${refused.length > 1 ? "s" : ""} : ${refused.map(({ number }) => number).join(", ")}`,
      message: reasons, type: "warning", duration: 8000 });
    await afterSend(null);
    return launched ? true : null;
  };
  const requestedInvoice = searchParams.get("invoice_id");
  const requestedInvoiceIntent = location.state?.invoiceIntent || "view";
  const requestedInvoiceRequest = location.state?.invoiceRequest;
  // Détail déplié : il suit le relevé choisi (autre carte, « Aller au suivant »).
  const selectInvoiceSummary = (invoiceId) => {
    if (requestedInvoice) return openInvoice(invoiceId);
    setInvoiceSummaryId((current) => current === invoiceId ? null : invoiceId);
  };
  React.useEffect(() => { if (requestedInvoice) setInvoiceSummaryId(requestedInvoice); }, [requestedInvoice]);
  React.useEffect(() => {
    let cancelled = false;
    setInvoiceDetail(null);
    if (!requestedInvoice) { setBusy(false); return; }
    setBusy(true);
    setInvoiceIntent(requestedInvoiceIntent);
    fetchInvoice(requestedInvoice).then((invoice) => {
      if (!cancelled) setInvoiceDetail(invoice);
    }).catch((actionError) => {
      if (!cancelled) showToast({ title: "Le relevé de transport ne peut pas être ouvert", message: actionError.message || "Une erreur est survenue.", type: "error", duration: 8000 });
    }).finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [requestedInvoice, requestedInvoiceIntent, requestedInvoiceRequest, showToast]);
  const previewDrafts = async (itemIdsArg) => {
    const itemIds = Array.isArray(itemIdsArg) && itemIdsArg.length ? [...itemIdsArg] : [...selected];
    if (!itemIds.length) return;
    setSelected(new Set(itemIds));
    let draftSelection;
    const applyPreviewReport = async (report) => {
      const blockingIds = new Set(
        draftPreviewBlockingItems(report)
          .map((item) => clean(item?.id || item?.entity_id))
          .filter(Boolean),
      );
      if (blockingIds.size) {
        setSelected((current) => new Set([...current].filter((itemId) => !blockingIds.has(clean(itemId)))));
      }
      setPreview({ ...report, draftSelection });
      void loadView({ silent: true });
    };
    setBusy(true);
    try {
      draftSelection = buildBillingDraftSelection(rows, itemIds, { from: filters.fromDate, to: filters.toDate });
      await applyPreviewReport(await previewBillingDrafts(draftSelection));
    }
    catch (actionError) {
      const report = actionError.payload?.detail;
      if (report && typeof report === "object" && Array.isArray(report.items)) await applyPreviewReport(report);
      else notifyError("La sélection ne peut pas être vérifiée", actionError);
    } finally { setBusy(false); }
  };
  const createDrafts = () => {
    if (!preview?.draftSelection) return;
    const draftSelection = preview.draftSelection;
    return run(
      () => createBillingDrafts({ ...draftSelection, idempotency_key: crypto.randomUUID?.() || `${Date.now()}` }),
      addsToExistingDrafts(preview) ? "Prestations ajoutées au brouillon" : "Brouillons créés",
      async () => { setPreview(null); setSelected(new Set()); changeTab("invoices"); },
    );
  };
  const saveAccount = async () => {
    await reloadDirectory();
    // Entreprise créée depuis « Associer » : la carte prend aussitôt son nom et ses prestations recalculées.
    if (activeTab === "billable") await loadView({ silent: true });
    setAccountEditor(null);
    showToast({ title: "Entreprise de facturation enregistrée", type: "success" });
  };
  const reloadDirectory = React.useCallback(async () => {
    loadRequestGuardRef.current?.invalidate();
    setLoading(false); setRefreshing(false);
    const references = await loadReferences();
    setError("");
    return references;
  }, [loadReferences]);
  const associateDirectoryClient = async (client, account) => {
    const payload = billingDirectoryAssociationPayload(client, account);
    loadRequestGuardRef.current?.invalidate();
    referencesLoadSequenceRef.current += 1;
    await updateBillingAccountAssociations(payload);
  };
  const archiveAccount = (account) => run(
    () => archiveBillingAccount(account.id, { version: account.version }),
    "Entreprise de facturation archivée",
    async () => { setAccountEditor(null); await reloadDirectory(); },
  );
  const saveIssuer = async (saved) => {
    setIssuer(saved);
    setIssuerSnapshot(saved);
    showToast({ title: "Paramètres enregistrés", type: "success" });
  };
  const changeLogo = (action, file) => run(
    () => action === "upload" ? uploadIssuerLogo(file) : deleteIssuerLogo(),
    action === "upload" ? "Logo enregistré" : "Logo retiré",
    async () => {
      const settings = await fetchIssuerSettings();
      setIssuerSnapshot(settings || {});
      setIssuer(settings);
    },
  );
  const invoiceAction = async (action, payload = {}) => {
    const invoice = invoiceDetail;
		if (action === "recheck-issue") {
			setBusy(true);
			try { setInvoiceDetail(await fetchInvoice(invoice.id)); }
			catch (actionError) { notifyError("Le relevé de transport ne peut pas être relu", actionError); }
			finally { setBusy(false); }
			return null;
		}
		if (action === "open-linked") {
			openInvoice(payload.invoiceId);
			return null;
		}
    if (action === "download-sent") return run(() => downloadInvoicePdf(invoice.id, { documentId: payload.documentId, filename: `${invoice.invoice_number || "releve"}.pdf` }), "PDF envoyé téléchargé", async () => {});
		if (action === "remove-item") {
			setBusy(true);
			try {
				const result = await removeDraftInvoiceItem(invoice.id, payload.billableItemId, payload.expectedVersion);
				if (result.invoice) setInvoiceDetail(result.invoice);
				else closeInvoice(true);
				showToast({
					title: "Ligne retirée du brouillon",
					type: "success",
				});
				void loadView({ silent: true });
				return result;
			} catch (actionError) {
				notifyError("La ligne n’a pas pu être retirée", actionError);
				return null;
			} finally { setBusy(false); }
		}
		if (action === "remove-manual-line") {
			setBusy(true);
			try {
				const result = await removeDraftManualLine(invoice.id, payload.lineId, payload.expectedVersion);
				setInvoiceDetail(result.invoice);
				showToast({ title: "Ligne libre retirée", type: "success" });
				void loadView({ silent: true });
				return result;
			} catch (actionError) {
				notifyError("La ligne libre n’a pas pu être retirée", actionError);
				return null;
			} finally { setBusy(false); }
		}
    const operations = {
      delete: () => deleteDraftInvoice(invoice.id),
			"save-draft": () => updateDraftInvoice(invoice.id, payload),
			"add-manual-line": () => createDraftManualLine(invoice.id, payload),
      void: () => voidInvoice(invoice.id, payload),
      period: () => changeInvoicePeriod(invoice.id, payload),
      "order-rates": () => changeInvoiceOrderRates(invoice.id, payload.orderId, payload.rates),
    };
    const messages = { delete: "Brouillon supprimé", "save-draft": "Brouillon enregistré", "add-manual-line": "Ligne libre ajoutée", void: payload?.notify_client ? "Relevé annulé ; l’entreprise sera prévenue par courriel" : "Relevé annulé", period: "Période du relevé modifiée", "order-rates": "Tarif modifié ; relevé revalidé" };
    // These responses already are the up-to-date invoice: no second read.
    const returnsInvoice = new Set(["save-draft", "add-manual-line", "void"]);
    return run(operations[action], messages[action], async (result) => {
      if (action === "delete") closeInvoice(true);
      else if (returnsInvoice.has(action) && result?.id === invoice.id) setInvoiceDetail(result);
      else setInvoiceDetail(await fetchInvoice(invoice.id));
      // The list refreshes in the background; the buttons are usable right away.
      void loadView({ silent: true });
    });
  };

  const directory = React.useMemo(() => buildBillingDirectory({ accounts, sourceClients }), [accounts, sourceClients]);
  const setupIncomplete = isBillingSetupIncomplete(issuerSnapshot, accounts);
  const setupReady = issuerSnapshotReady && referencesReady;
  React.useEffect(() => {
    if (!setupReady || setupHomeLocked !== null) return;
    setSetupHomeLocked(setupIncomplete);
  }, [setupReady, setupIncomplete, setupHomeLocked]);
  const showSetupHome = setupReady && (setupIncomplete || setupHomeLocked);
  const continueFromSetup = () => {
    if (isBillingSetupIncomplete(issuerSnapshot, accounts)) return;
    setSetupHomeLocked(false);
    changeTab("billable");
  };

  if (!preferencesReady || !setupReady) return <div className="grid h-full min-h-96 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><LoaderCircle className="animate-spin" size={18} />Chargement des préférences de facturation…</span></div>;
  if (showSetupHome && activeTab !== "accounts") {
    return (
      <div className="flex h-full min-h-[650px] w-full flex-col px-1 pb-3 text-slate-800 sm:px-2">
        <BillingHomeView
          issuer={issuerSnapshot}
          accounts={accounts}
          canEdit={permissions.settings}
          busy={busy}
          onOpenIssuer={openIssuer}
          onAddAccount={() => setAccountEditor({})}
          onOpenDirectory={() => {
            updateBillingPreferences((current) => ({ ...current, accountsFilters: { ...createDefaultAccountsFilters(), directoryStatus: "needs_setup" } }));
            changeTab("accounts");
          }}
          onContinue={continueFromSetup}
        />
        {accountEditor ? <AccountEditor key={accountEditor.id || "new"} account={accountEditor} accounts={accounts} sourceClients={sourceClients} busy={busy} canEdit={permissions.settings} onClose={() => setAccountEditor(null)} onSaved={saveAccount} onReload={reloadDirectory} onArchive={archiveAccount} onGuardChange={onAssociationsGuardChange} /> : null}
        {issuer ? <IssuerSettingsModal settings={issuer} busy={busy} canEdit={permissions.settings} onClose={() => setIssuer(null)} onSaved={saveIssuer} onLogoChanged={changeLogo} /> : null}
      </div>
    );
  }
  // Filtres de départ d'« À facturer » : « Aucune prestation prête depuis le … » et « Tous les états ».
  const atDefaultFilters = activeTab === "billable" && isDefaultBillableFilters(filters);
  const hasFilters = !atDefaultFilters && Boolean(filters.search || filters.clientId || filters.clientName || filters.billingAccountId || filters.billingSide || filters.fromDate || filters.toDate || filters.status || filters.overdueOnly || filters.blockingReason);
  const empty = billingEmptyState(activeTab, { hasFilters, defaultSince: atDefaultFilters ? filters.fromDate : "" });
  const billableRows = activeTab === "billable"
    ? filterBillableItemsByOverdue(rows, { overdueOnly: filters.overdueOnly })
    : rows;
  const billableStatusFilters = BILLABLE_STATUS_FILTERS.map((option) => ({
    ...option,
    activeTone: option.value === "blocked" ? "warning" : "accent",
  }));
  const blockingReasonOptions = billableBlockedReasonFilterOptions({
    reasonCounts: billableKpis.blockingReasonCounts || [],
    approvedBlockedCount: billableKpis.approvedBlockedCount,
    selectedReason: filters.blockingReason,
  });


  // Bande ardoise commune avec la Consolidation ; fond gris et blocs blancs comme elle.
  const listView = activeTab === "billable" || activeTab === "invoices";
  return <div className="billing-page flex h-full min-h-[650px] w-full flex-col gap-2 px-1 pb-3 text-slate-800 sm:px-2">
    <WorkspaceBand icon={FileText} title="Facturation" tabsLabel="Facturation" subtitle={<p>Prestations, relevés et entreprises de facturation</p>}
      tabs={TABS.map((tab) => <WorkspaceTab key={tab.key} icon={tab.icon} active={activeTab === tab.key} disabled={busy} onClick={() => changeTab(tab.key)}>{tab.label}</WorkspaceTab>)}
      tabsRight={<><WorkspaceSummary section="consolidation" /><button type="button" className={`${SECONDARY_BUTTON} h-8 px-2.5 text-xs`} disabled={busy} onClick={openIssuer}><UserRound size={13} />Paramètres</button><button type="button" className={`${SECONDARY_BUTTON} h-8 px-2.5 text-xs`} disabled={loading || refreshing || busy} onClick={() => loadView({ silent: true })}><RefreshCw className={refreshing ? "animate-spin" : ""} size={13} />Actualiser</button></>} />
    <section className="relative flex min-h-0 flex-1 flex-col gap-2 overflow-hidden" aria-busy={loading || refreshing}>
      {activeTab === "billable" ? (
        <BillableKpiStrip
          busy={busy || loading}
          filters={filters}
          kpis={billableKpis}
          onSelect={applyBillableKpiFilter}
        />
      ) : null}
		{activeTab === "invoices" ? <InvoiceKpiStrip busy={busy || loading} invoices={rows} month={periodMonth(filters.fromDate, filters.toDate) || String(invoiceSummary.as_of_date || meta.as_of_date || "").slice(0, 7)} onSelect={selectInvoiceSummary} /> : null}
      {listView ? <div className="flex shrink-0 flex-col overflow-hidden rounded-[4px] border border-[#dfe5e2] bg-white">
      {activeTab === "billable" ? (
        <StatusSubNav
          options={billableStatusFilters}
          value={filters.overdueOnly ? "__overdue__" : filters.status}
          disabled={busy}
          reasonOptions={filters.status === "blocked" && !filters.overdueOnly ? blockingReasonOptions : []}
          reasonValue={filters.blockingReason}
          onReasonChange={applyBlockingReasonFilter}
          onChange={(nextStatus) => {
            if (nextStatus === "__overdue__") return;
            updateFilter("status", nextStatus);
          }}
        />
      ) : null}
      {activeTab !== "accounts" ? <Filters activeTab={activeTab} sourceClients={sourceClients} clientNames={invoiceSummary.client_names || []} accounts={accounts} filters={filters} onChange={updateFilter} onReset={resetFilters} /> : null}
      {activeTab === "billable" ? <p className="shrink-0 border-b border-slate-100 px-3 py-1.5 text-[11px] text-slate-600">{filters.status === "blocked" ? "Prestations bloquées : complétez les informations de facturation indiquées." : filters.status === "eligible" ? "Prestations prêtes à facturer." : "Prestations prêtes et bloquées selon les filtres sélectionnés."}</p> : null}
      </div> : null}
      {/* Les cartes de prestations et de relevés posent sur le fond gris ; les autres vues gardent un bloc blanc. */}
      <BillingFiltersContextMenu activeTab={activeTab} enabled={listView} busy={busy} filters={filters} onChange={updateFilter} onReset={resetFilters}
        sourceClients={sourceClients} clientNames={invoiceSummary.client_names || []} accounts={accounts}
        statusOptions={billableStatusFilters} reasonOptions={blockingReasonOptions} onReasonChange={applyBlockingReasonFilter}
        className={listView && !error && !loading && (billableRows.length || (activeTab !== "billable" && rows.length)) ? "flex min-h-0 flex-1 flex-col" : "flex min-h-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-[#dfe5e2] bg-white"}>
      {error ? <EmptyState error={error} onRetry={() => loadView()} /> : loading ? <EmptyState loading /> : activeTab === "accounts" ? (
          <BillingDirectoryView directory={directory} accounts={accounts} search={billingPreferences.accountsFilters.search}
            type={billingPreferences.accountsFilters.directoryType}
            onTypeChange={(directoryType) => updateBillingPreferences((current) => ({ ...current, accountsFilters: { ...current.accountsFilters, directoryType } }))}
            status={billingPreferences.accountsFilters.directoryStatus} onSearch={updateAccountsSearch} onStatusChange={updateDirectoryStatus}
            canEdit={permissions.settings} busy={busy} onEdit={setAccountEditor} onCreate={(client) => setAccountEditor(billingDirectoryAccountDraft(client))}
            onAssociate={associateDirectoryClient} onReload={reloadDirectory} onArchive={archiveAccount}
            canEditClients={hasPermission(ACCESS_PERMISSION_KEYS.dataManagerClientCorrespondencesWrite)}
            onUnmarkClient={(client) => run(() => updateClientCorrespondence(client.id, { is_coop: false }), "Retiré des entreprises facturées", reloadDirectory)}
            onDeleteClient={(client) => run(() => deleteClientCorrespondence(client.id, {}), "Fiche Clients supprimée", reloadDirectory)} />
      ) : billableRows.length || (activeTab !== "billable" && rows.length) ? <div className="flex min-h-0 flex-1 flex-col">{activeTab === "billable" ? <BillableAccountCards items={billableRows} canDraft={permissions.drafts} busy={busy} onCreate={previewDrafts} sourceClients={sourceClients} billingAccounts={accounts} canAssociate={permissions.settings} onAssociate={associateDirectoryClient} onCreateAccount={(client) => setAccountEditor(billingDirectoryAccountDraft(client))} onReload={async () => { await reloadDirectory(); await loadView({ silent: true }); }} /> : <InvoiceCards invoices={rows} busy={busy} permissions={permissions} onAction={cardAction} onOpen={openInvoice} onSelect={selectInvoiceSummary} selectedInvoiceId={invoiceSummaryId} refreshKey={rows}
        detailId={requestedInvoice} detailBlock={detailSendBlock} onCloseDetail={closeInvoice} onSendEmailDirtyChange={setSendEmailDirty}
        detail={invoiceDetail?.id === requestedInvoice ? <InvoiceDetail key={`${invoiceDetail.id}:${invoiceDetail.version}`} invoice={invoiceDetail} intent={invoiceIntent} busy={busy || invoiceSync.updating} permissions={permissions} onAction={invoiceAction} onDirtyChange={setInvoiceDirty} onSendBlockChange={setDetailSendBlock} guideStarted={detailGuideStarted} sync={invoiceSync} view={detailView} /> : null} />}<Pager meta={meta} count={activeTab === "billable" ? billableRows.length : rows.length} onPage={(cursor) => updateFilter("cursor", cursor)} /></div> : <EmptyState message={empty.title} description={empty.message} onReset={empty.canResetFilters ? resetFilters : undefined}
        action={empty.showAllStates ? { label: "Tous les états", onClick: () => updateFilter("status", "") } : null} />}
      </BillingFiltersContextMenu>
    </section>
    {preview ? <DraftPreviewModal preview={preview} busy={busy} onClose={() => setPreview(null)} onConfirm={createDrafts} /> : null}
    {accountEditor ? <AccountEditor key={accountEditor.id || "new"} account={accountEditor} accounts={accounts} sourceClients={sourceClients} busy={busy} canEdit={permissions.settings} onClose={() => setAccountEditor(null)} onSaved={saveAccount} onReload={reloadDirectory} onArchive={archiveAccount} onGuardChange={onAssociationsGuardChange} /> : null}
    {issuer ? <IssuerSettingsModal settings={issuer} busy={busy} canEdit={permissions.settings} onClose={() => setIssuer(null)} onSaved={saveIssuer} onLogoChanged={changeLogo} /> : null}
  </div>;
}
